import { z } from "zod";
import type { LoginManager } from "./LoginManager";
import {
  batch,
  computed,
  effect,
  signal,
  untracked,
  type ReadonlySignal,
  type Signal,
} from "@preact/signals";
import { listAllByAddress, type CasualOSManager } from "./OsManager";
import type { ReaderTab, TabsManager } from "./TabsManager";
import { v4 as uuid } from "uuid";
import type { NavigationManager } from "./NavigationManager";
import { parseNumber } from "./Utils";
import type { ModalManager } from "./ModalManager";
import type { ChatsManager } from "./ChatsManager";
import { openPlaylistItemPreview } from "../components/playlistItemPreview";
import { PlaylistFinishedModalContent } from "../components/PlaylistFinishedModal/PlaylistFinishedModal";
import type { I18nManager } from "../i18n";
import type {
  BibleReadingExtensionManager,
  ReadingExtensionInstance,
} from "./BibleReadingExtensionManager";
import {
  AIPlaylistItemSchema,
  convertToAiPlaylistItem,
  convertToPlaylistItem,
  generateFunctionTool,
  type GeneratedPlaylist,
} from "./AIManager";
import type { DiscoverManager } from "./DiscoverManager";
import { emphasizeVerses } from "./BibleReadingManager";
import {
  BOOK_SLUGS,
  type BookId,
  type VerseRef as ReaderVerseRef,
} from "./BibleDataManager";
import { addCivilDays, civilDateInZone, civilDateToISO } from "./civilDate";
import { savePhotoToGallery } from "./UserGalleryManager";
import {
  createFriendContentFreshness,
  isFriendContentStale,
  SkippedFriendRead,
  type FriendReadLimiter,
} from "./friendContentFreshness";
import {
  buildSharedPagePath,
  parsePlaylistPagePath,
  parseRecordLocator,
} from "./SharedPagePath";
import {
  createSharedPageLoader,
  loadSharedPageAuthorName,
  type SharedPage,
  type SharedPageSeed,
} from "./SharedPageLoader";
import {
  carryOverLinkPreview,
  createLinkPreviewLoader,
  LinkPreviewSchema,
} from "./linkPreview";

export const VerseRefSchema = z.object({
  bookId: z.string(),
  chapter: z.number().positive(),
  endChapter: z.number().positive().optional(),
  verse: z.number().positive().optional(),
  endVerse: z.number().positive().optional(),
  /**
   * Internal marker set only on queue fragments synthesized by
   * `expandCrossChapterItem` (never written by the playlist editor, never
   * part of a saved item): highlight from `verse` through however many
   * verses this chapter actually has. Resolved lazily in
   * `navigateToCurrentItem` from the chapter data navigation already loads,
   * since the count isn't known synchronously at queue-build time.
   */
  toEndOfChapter: z.boolean().optional(),
});

export const BibleVersePlaylistItem = z.object({
  type: z.literal("bible-verse"),
  ref: VerseRefSchema,
  translationId: z.string().optional(),
  /**
   * The author's formatted note on the passage (sanitized HTML, like an `html`
   * item). Shown alongside the passage while it plays rather than in a modal.
   */
  note: z.string().optional(),
  /** Optional plain-text heading for the note, shown above it. */
  noteTitle: z.string().optional(),
});

export const PlaylistItem = z.discriminatedUnion("type", [
  BibleVersePlaylistItem,
  z.object({
    type: z.literal("html"),
    title: z.string().optional(),
    html: z.string(),
  }),
  z.object({
    type: z.literal("link"),
    title: z.string().optional(),
    url: z.url(),
    /**
     * When true, the play view embeds the URL in an iframe instead of showing
     * an "Open" link. Video URL detection still takes precedence (see
     * {@link resolveLinkMedia}).
     */
    embed: z.boolean().optional(),
    /** The page's title, description, and image, fetched when it was saved. */
    preview: LinkPreviewSchema.optional(),
  }),
]);

export const PlaylistSchema = z.object({
  id: z.string(),
  recordName: z.string(),
  authorUserId: z.string(),
  title: z.string().nullable(),
  description: z.string().nullable(),
  /**
   * Public URL of a 4:3 cover image. Optional so playlists saved before this
   * field existed still parse.
   */
  heroImageUrl: z.url().max(2048).nullable().optional(),
  items: z.array(PlaylistItem),
  createdAtMs: z.number().positive(),
  updatedAtMs: z.number().positive(),
});

function clonePlaylist(playlist: Playlist): Playlist {
  return PlaylistSchema.parse(JSON.parse(JSON.stringify(playlist)));
}

/**
 * Fields the unsaved-changes prompt cares about: name, description, cover, items.
 * Link previews are left out — they are fetched, not edited, so one filling in
 * after the editor opens isn't a change the author needs to be asked about.
 */
function playlistEditorState(playlist: Playlist): string {
  return JSON.stringify({
    title: playlist.title ?? null,
    description: playlist.description ?? null,
    heroImageUrl: playlist.heroImageUrl ?? null,
    items: playlist.items.map((item) =>
      item.type === "link" ? { ...item, preview: undefined } : item
    ),
  });
}

function getPlaylistLocator(playlist: {
  recordName?: string;
  id: string;
}): string {
  return `${playlist.recordName ?? ""}.${playlist.id}`;
}

export type Playlist = z.infer<typeof PlaylistSchema>;
export type SimplePlaylist = Pick<
  Playlist,
  "id" | "items" | "title" | "description" | "heroImageUrl"
> &
  /**
   * Who made it, when someone did: a saved playlist, or the reading plan a
   * day's queue came from. Absent for a queue no one authored, such as an
   * AI-generated playlist played before it's saved.
   */
  Partial<Pick<Playlist, "authorUserId">>;

/** A shared `/{lang}/playlist/...` page's playlist and its author. */
export type PlaylistPage = SharedPage<Playlist>;
/** A completed playlist-page load, embedded by SSR — see `SharedPageSeed`. */
export type PlaylistPageSeed = SharedPageSeed<Playlist>;

/**
 * True when a playlist is backed by a record, so a play-history row can reload
 * it later by `{recordName, id}`. Ad-hoc queues (a reading plan's day, say)
 * carry no record name and so can't be resumed from history.
 */
function isRecordedPlaylist(
  playlist: SimplePlaylist
): playlist is SimplePlaylist & Pick<Playlist, "recordName"> {
  return typeof (playlist as Partial<Playlist>).recordName === "string";
}
export type PlaylistItemData = z.infer<typeof PlaylistItem>;
export type VerseRef = z.infer<typeof VerseRefSchema>;

/**
 * One verse or whole-chapter selection to collapse into playlist items.
 * `verse` omitted means the whole chapter, matching {@link VerseRefSchema}.
 */
export interface PlaylistVerseSelection {
  bookId: string;
  chapter: number;
  verse?: number;
}

/** Canonical Protestant + Apocrypha order from {@link BOOK_SLUGS} insertion order. */
const CANONICAL_BOOK_ORDER = new Map(
  (Object.keys(BOOK_SLUGS) as BookId[]).map((id, index) => [id, index])
);

function canonicalBookIndex(bookId: string): number {
  return CANONICAL_BOOK_ORDER.get(bookId as BookId) ?? Number.MAX_SAFE_INTEGER;
}

function comparePlaylistVerseSelections(
  a: PlaylistVerseSelection,
  b: PlaylistVerseSelection
): number {
  const bookDiff = canonicalBookIndex(a.bookId) - canonicalBookIndex(b.bookId);
  if (bookDiff !== 0) {
    return bookDiff;
  }
  if (a.bookId !== b.bookId) {
    return a.bookId.localeCompare(b.bookId);
  }
  if (a.chapter !== b.chapter) {
    return a.chapter - b.chapter;
  }
  // Whole-chapter selections (no verse) sort before verses in that chapter so
  // they never get mixed into a verse run.
  return (a.verse ?? 0) - (b.verse ?? 0);
}

function selectionKey(selection: PlaylistVerseSelection): string {
  return `${selection.bookId}:${selection.chapter}:${selection.verse ?? "chapter"}`;
}

/**
 * True when `curr` is the next verse or chapter after `prev` in reading order.
 * Cross-chapter verse runs only join when `verseCountOf` says `prev` was the
 * last verse of its chapter and `curr` is verse 1 of the next — without that
 * count we cannot tell Genesis 1:5 + 2:1 from a real chapter-boundary range.
 */
function isContinuousPlaylistSelection(
  prev: PlaylistVerseSelection,
  curr: PlaylistVerseSelection,
  verseCountOf?: (bookId: string, chapter: number) => number | undefined
): boolean {
  if (prev.bookId !== curr.bookId) {
    return false;
  }

  const prevIsChapter = prev.verse == null;
  const currIsChapter = curr.verse == null;
  if (prevIsChapter !== currIsChapter) {
    return false;
  }

  if (prevIsChapter) {
    return curr.chapter === prev.chapter + 1;
  }

  if (curr.chapter === prev.chapter) {
    return curr.verse === prev.verse! + 1;
  }

  if (curr.chapter === prev.chapter + 1 && curr.verse === 1) {
    const lastVerse = verseCountOf?.(prev.bookId, prev.chapter);
    return lastVerse != null && prev.verse === lastVerse;
  }

  return false;
}

function playlistItemFromSelectionGroup(
  group: PlaylistVerseSelection[]
): PlaylistItemData {
  const start = group[0]!;
  const end = group[group.length - 1]!;
  const spansChapters = end.chapter !== start.chapter;

  if (start.verse == null) {
    return {
      type: "bible-verse",
      ref: {
        bookId: start.bookId,
        chapter: start.chapter,
        ...(spansChapters ? { endChapter: end.chapter } : {}),
      },
    };
  }

  const spansVerses = spansChapters || end.verse !== start.verse;

  return {
    type: "bible-verse",
    ref: {
      bookId: start.bookId,
      chapter: start.chapter,
      verse: start.verse,
      ...(spansChapters ? { endChapter: end.chapter } : {}),
      ...(spansVerses ? { endVerse: end.verse } : {}),
    },
  };
}

/**
 * Collapses a bag of selected verses (and, later, whole chapters) into one
 * playlist item per contiguous range. Sorted into canonical book order, then
 * chapter, then verse; gaps and book changes start a new item.
 *
 * `verseCountOf` is optional: when the caller knows how many verses a chapter
 * has, a run that ends on that last verse and continues at verse 1 of the next
 * chapter becomes one cross-chapter item (`endChapter` / `endVerse` on
 * {@link VerseRefSchema}).
 */
export function groupVersesIntoPlaylistItems(
  verses: readonly PlaylistVerseSelection[],
  verseCountOf?: (bookId: string, chapter: number) => number | undefined
): PlaylistItemData[] {
  if (verses.length === 0) {
    return [];
  }

  const seen = new Set<string>();
  const unique: PlaylistVerseSelection[] = [];
  for (const verse of verses) {
    const key = selectionKey(verse);
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    unique.push(verse);
  }

  unique.sort(comparePlaylistVerseSelections);

  const groups: PlaylistVerseSelection[][] = [];
  let current: PlaylistVerseSelection[] = [];
  for (const verse of unique) {
    const prev = current[current.length - 1];
    if (!prev || isContinuousPlaylistSelection(prev, verse, verseCountOf)) {
      current.push(verse);
    } else {
      groups.push(current);
      current = [verse];
    }
  }
  if (current.length > 0) {
    groups.push(current);
  }

  return groups.map(playlistItemFromSelectionGroup);
}

/**
 * Latest play of a playlist, stored under the user's record so shared playlist
 * links can be reopened later from Discover without keeping the URL. One row
 * per playlist: playing again (resume or start over) overwrites that row
 * instead of appending a session log.
 */
export const PlaylistPlayHistorySchema = z.object({
  id: z.string(),
  recordName: z.string(),
  userId: z.string(),
  playlistId: z.string(),
  playlistRecordName: z.string(),
  playlistTitle: z.string().nullable(),
  playlistDescription: z.string().nullable().optional(),
  playlistHeroImageUrl: z.url().max(2048).nullable().optional(),
  /**
   * Legacy chain to a prior play-session. Kept so stored records still parse;
   * new writes leave it unset. History is one row per playlist now.
   */
  previousHistoryId: z.string().nullable().optional(),
  totalSteps: z.number().int().nonnegative(),
  /** 0-based queue index at last update; `-1` when the queue was empty. */
  currentStep: z.number().int(),
  /** Snapshot of the queue item at `currentStep` for display without reloading. */
  lastItem: PlaylistItem.nullable(),
  startedAtMs: z.number().positive(),
  /** Set when playback stops; `null` while this session is still active. */
  endedAtMs: z.number().positive().nullable().optional(),
  /** Wall-clock ms from `startedAtMs` to the last save (or stop). */
  durationMs: z.number().nonnegative().default(0),
  createdAtMs: z.number().positive(),
  updatedAtMs: z.number().positive(),
});
export type PlaylistPlayHistory = z.infer<typeof PlaylistPlayHistorySchema>;

/**
 * Options for {@link createPlaylistManager}'s `startPlaying`. Pass
 * `history: false` to skip tracking (e.g. reading plans that reuse the
 * playlist player). Pass `sharedOpening: true` when the play came from a
 * shared link, a reload of a playing URL, or the playlist page's Start
 * button; closing the opening text or link then walks into the playlist.
 */
export type StartPlayingOptions = {
  history?: false;
  sharedOpening?: true;
};

export type PlaylistManager = ReturnType<typeof createPlaylistManager>;
export type PlayingState = ReturnType<typeof createPlayingState>;

const PLAYLIST_PLAY_HISTORY_MARKER = "publicRead:playlistPlayHistory";

/**
 * Bound on how many playlists we keep in history. One row per playlist, so
 * this is a cap on distinct playlists, not on play sessions.
 */
export const MAX_PLAYLIST_PLAY_HISTORY = 200;

/** Identity of the playlist a history row refers to (record + id). */
export function playlistPlayHistoryKey(
  entry: Pick<PlaylistPlayHistory, "playlistRecordName" | "playlistId">
): string {
  return `${entry.playlistRecordName}.${entry.playlistId}`;
}

/**
 * Newest play per playlist, newest-first, capped at
 * {@link MAX_PLAYLIST_PLAY_HISTORY}. Used both when loading from the backend
 * and when pruning after a new play so Discover never shows a session log.
 */
export function retainPlaylistPlayHistory(
  entries: PlaylistPlayHistory[]
): PlaylistPlayHistory[] {
  const sorted = [...entries].sort((a, b) => b.startedAtMs - a.startedAtMs);
  const seen = new Set<string>();
  const retained: PlaylistPlayHistory[] = [];
  for (const entry of sorted) {
    const key = playlistPlayHistoryKey(entry);
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    retained.push(entry);
    if (retained.length >= MAX_PLAYLIST_PLAY_HISTORY) {
      break;
    }
  }
  return retained;
}

export type PlaylistPlayHistoryDayGroup = {
  dayKey: string;
  entries: PlaylistPlayHistory[];
};

/**
 * Newest-first history rows grouped by calendar day in `timeZone` (the
 * machine zone when omitted). Day keys are `YYYY-MM-DD`.
 */
export function groupPlaylistPlayHistoryByDay(
  entries: PlaylistPlayHistory[],
  timeZone?: string | null
): PlaylistPlayHistoryDayGroup[] {
  const sorted = retainPlaylistPlayHistory(entries);
  const groups = new Map<string, PlaylistPlayHistory[]>();
  const order: string[] = [];
  for (const entry of sorted) {
    const dayKey = civilDateToISO(civilDateInZone(entry.startedAtMs, timeZone));
    const group = groups.get(dayKey);
    if (group) {
      group.push(entry);
      continue;
    }
    groups.set(dayKey, [entry]);
    order.push(dayKey);
  }
  return order.map((dayKey) => ({
    dayKey,
    entries: groups.get(dayKey) ?? [],
  }));
}

/** Which heading a history day group should use. */
export function playlistPlayHistoryDayKind(
  dayKey: string,
  nowMs: number = Date.now(),
  timeZone?: string | null
): "today" | "yesterday" | "date" {
  const today = civilDateToISO(civilDateInZone(nowMs, timeZone));
  if (dayKey === today) {
    return "today";
  }
  const yesterday = civilDateToISO(
    addCivilDays(civilDateInZone(nowMs, timeZone), -1)
  );
  if (dayKey === yesterday) {
    return "yesterday";
  }
  return "date";
}

/** Fraction of the queue reached (0–1), counting every item type. */
export function playlistPlayHistoryPercent(
  entry: Pick<PlaylistPlayHistory, "currentStep" | "totalSteps">
): number {
  if (entry.totalSteps <= 0 || entry.currentStep < 0) {
    return 0;
  }
  const reached = Math.min(entry.currentStep + 1, entry.totalSteps);
  return reached / entry.totalSteps;
}

/** True when the last update was on the final queue item. */
export function isPlaylistPlayHistoryComplete(
  entry: Pick<PlaylistPlayHistory, "currentStep" | "totalSteps">
): boolean {
  return entry.totalSteps > 0 && entry.currentStep >= entry.totalSteps - 1;
}

/** Shown when a history row's playlist cannot be loaded. One string for every caller. */
export function playlistHistoryOpenFailedMessage(t: {
  (key: string, options?: { defaultValue?: string }): string;
}): string {
  return t("playlist-history-open-failed", {
    defaultValue: "Couldn't open that playlist. It may have been deleted.",
  });
}

/**
 * Formats a wall-clock play duration for the history accordion. Kept here so
 * manager tests can assert the same string the UI shows.
 */
export function formatPlaylistPlayDurationMs(ms: number): string {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  if (minutes > 0) {
    return `${minutes}m ${seconds}s`;
  }
  return `${seconds}s`;
}

/**
 * Duration shown in Discover: finished sessions use the saved `durationMs`;
 * an still-active session (no `endedAtMs`) is measured live from `startedAtMs`
 * so the accordion stays honest without a backend write every few seconds.
 */
export function playlistPlayHistoryDisplayDurationMs(
  entry: Pick<PlaylistPlayHistory, "startedAtMs" | "endedAtMs" | "durationMs">,
  nowMs: number = Date.now()
): number {
  if (entry.endedAtMs == null) {
    return Math.max(0, nowMs - entry.startedAtMs);
  }
  return entry.durationMs;
}

/**
 * The serializable playback state the playlist reading extension stores in its
 * per-enablement `data`. Kept as plain JSON so it can be mirrored across a
 * shared session (see `SessionsManager`). The live {@link PlayingState} (which
 * holds signals and effects) is built from this and never stored here.
 *
 * `queue` is a copy of the source playlists' items, so it can be reordered or
 * have items added/removed without mutating `playlists`; both are synced.
 */
export interface PlaylistReadingData {
  playlists: SimplePlaylist[];
  queue: PlaylistItemData[];
  step: number;
  /**
   * A passage the current step's note links to that the reader has been taken
   * to, or null when the reader is on the step (see
   * `PlayingState.visitReference`). Synced like the step, so a shared session
   * follows the detour together and anyone can bring everyone back.
   */
  detour?: ReaderVerseRef | null;
}

/**
 * The reading-extension instance the playlist extension returns from
 * `activate()`. It carries the live {@link PlayingState} so `PlaylistManager`
 * can read it off the enabled runtime instead of storing playback itself.
 */
export interface PlaylistReadingExtensionInstance extends ReadingExtensionInstance<PlaylistReadingData> {
  playingState: PlayingState;
}

/** Structural equality via JSON, used to guard the playback sync effects. */
function jsonEqual(a: unknown, b: unknown): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

/** No book of the Bible (Psalms, the longest) has more than 150 chapters. */
const MAX_CHAPTER_NUMBER = 150;

/**
 * Expands a `bible-verse` item spanning multiple chapters into one item per
 * chapter in the range, so playback (queue stepping, highlighting) advances
 * chapter by chapter instead of only ever visiting the first chapter. Other
 * item types, and bible-verse items that don't cross a chapter boundary, pass
 * through unchanged.
 *
 * Exported because callers that pass a `startIndex` to `startPlaying` have to
 * count that index in the same expanded steps this produces — a list of
 * readings and the queue built from it are not the same length.
 */
export function expandCrossChapterItem(
  item: PlaylistItemData
): PlaylistItemData[] {
  if (item.type !== "bible-verse") {
    return [item];
  }

  const { ref, translationId, note, noteTitle } = item;
  const endChapter = ref.endChapter;
  if (
    endChapter == null ||
    endChapter <= ref.chapter ||
    endChapter > MAX_CHAPTER_NUMBER
  ) {
    return [item];
  }

  const items: PlaylistItemData[] = [];

  for (let chapter = ref.chapter; chapter <= endChapter; chapter++) {
    if (chapter === ref.chapter) {
      items.push({
        type: "bible-verse",
        translationId,
        // The note is about the whole passage, so it shows once, where the
        // passage starts, rather than again on every chapter of it.
        ...(note != null ? { note } : {}),
        ...(noteTitle != null ? { noteTitle } : {}),
        ref:
          ref.verse != null
            ? {
                bookId: ref.bookId,
                chapter,
                verse: ref.verse,
                toEndOfChapter: true,
              }
            : { bookId: ref.bookId, chapter },
      });
    } else if (chapter === endChapter) {
      items.push({
        type: "bible-verse",
        translationId,
        ref:
          ref.endVerse != null
            ? { bookId: ref.bookId, chapter, verse: 1, endVerse: ref.endVerse }
            : { bookId: ref.bookId, chapter },
      });
    } else {
      // Fully-included middle chapter: whole-chapter reference, no highlight.
      items.push({
        type: "bible-verse",
        translationId,
        ref: { bookId: ref.bookId, chapter },
      });
    }
  }

  return items;
}

/**
 * How playback arrived at the current step. Closing a text or link modal
 * continues in that direction. A direct landing — pressing play, jumping to
 * an item, or opening a link to that step — stays on the item, except the
 * first item, which always steps forward.
 */
export type PlaylistStepArrival = "forward" | "backward" | "direct";
/**
 * How long a passage stays highlighted after its note is closed, before the
 * highlight fades.
 */
export const NOTE_CLOSED_HIGHLIGHT_MS = 1500;

/** Whether a queue item is scripture carrying a note (a body, a title, or both). */
export function hasScriptureNote(
  item: PlaylistItemData | null
): item is Extract<PlaylistItemData, { type: "bible-verse" }> {
  return (
    item?.type === "bible-verse" &&
    !!(item.note?.trim() || item.noteTitle?.trim())
  );
}

/**
 * Creates an in-memory playing state for stepping through one or more
 * playlists. The queue is a copy of the source playlists' items, so
 * manipulating it never mutates the underlying playlists.
 *
 * When a `tab` is provided, advancing to a `bible-verse` item navigates that
 * tab's reader to the verse. The tab is the one saved when playback started, so
 * navigation keeps targeting it even if the user later switches tabs.
 */
export function createPlayingState(
  sourcePlaylists: SimplePlaylist[],
  tab: ReaderTab | null = null,
  onFinish?: () => void
) {
  const playlists = signal<SimplePlaylist[]>(sourcePlaylists);
  const queue = signal<PlaylistItemData[]>(
    sourcePlaylists.flatMap((playlist) =>
      playlist.items.flatMap(expandCrossChapterItem)
    )
  );
  const currentIndex = signal<number>(queue.value.length > 0 ? 0 : -1);

  /** The item at `currentIndex`, or null when the queue is empty. */
  const currentItem = computed<PlaylistItemData | null>(
    () => queue.value[currentIndex.value] ?? null
  );
  const hasNext = computed(() => currentIndex.value < queue.value.length - 1);
  const hasPrevious = computed(() => currentIndex.value > 0);
  /**
   * True on the last item of a saved playlist, where `next()` reports the
   * playlist finished (via `onFinish`) instead of doing nothing. Ad-hoc queues,
   * like a reading plan's day, keep their own progress and have no link to
   * share, so they never finish this way.
   */
  const canFinish = computed(
    () =>
      !!onFinish &&
      queue.value.length > 0 &&
      currentIndex.value === queue.value.length - 1 &&
      !!playlists.value[0] &&
      isRecordedPlaylist(playlists.value[0])
  );
  /** Whether pressing next does anything: moves on, or finishes the playlist. */
  const canPressNext = computed(() => hasNext.value || canFinish.value);
  /**
   * Whether `onFinish` has run since playback last reached the last item. The
   * reader's own next controls (keyboard, swipe) finish the playlist once, then
   * hand back to chapter-by-chapter reading so they don't dead end on the
   * modal. A signal so the swipe preview, which `null`s out the finishing
   * swipe, reloads the real next chapter once it has been shown. Moving to any
   * other item re-arms it.
   */
  const finishPromptShown = signal(false);
  /**
   * Whether the current step's note is open: the desktop popover shown, or the
   * mobile sheet expanded. Every step starts with it open. While it is, the
   * passage stays highlighted; see `setNoteExpanded`.
   */
  const noteExpanded = signal(true);
  /**
   * The passage a note's link took the reader to, while it's showing that
   * rather than the step's own (see `visitReference`), else null. Playback
   * carries on meanwhile; `returnToStep` or moving to any step brings the
   * reader back.
   */
  const detour = signal<ReaderVerseRef | null>(null);
  const awayFromStep = computed(() => detour.value !== null);
  let lastIndex = currentIndex.peek();
  let lastItem = currentItem.peek();
  const disposeRearm = effect(() => {
    const index = currentIndex.value;
    const item = currentItem.value;
    if (index !== lastIndex) {
      lastIndex = index;
      finishPromptShown.value = false;
    }
    // By the item, not its index: a queue edit can shift the step's index
    // without changing the step, or put a different item at the same index.
    // Compared by content, since a shared session delivers the same queue as
    // new objects.
    if (!jsonEqual(item, lastItem)) {
      lastItem = item;
      noteExpanded.value = true;
    }
  });

  let decorationId: string | null = null;
  /** See {@link PlaylistStepArrival}. Starts direct: playback opens on a step the user chose, it doesn't step there. */
  let stepArrival: PlaylistStepArrival = "direct";
  /**
   * Whether any step has been shown in the reader yet. Until one has, the
   * current step is shown even when the reader already sits on its chapter —
   * starting a playlist from the chapter its first passage is in must still
   * scroll to and highlight that passage.
   */
  let hasShownStep = false;
  /**
   * Counts navigations (to a step, or to a passage a note links to), so one
   * that finishes loading its chapter after a newer one started leaves the
   * highlight alone rather than putting back a passage that's been left.
   */
  let navigationCount = 0;

  /** Pending removal of a noted passage's highlight after its note closed. */
  let noteClosedTimer: ReturnType<typeof setTimeout> | null = null;

  const disposeDecoration = () => {
    if (noteClosedTimer) {
      clearTimeout(noteClosedTimer);
      noteClosedTimer = null;
    }
    if (tab && decorationId) {
      tab.readingState.removeDecoration(decorationId);
      decorationId = null;
    }
  };

  /**
   * Highlights `item`'s verses in the reader. A passage with a note stays
   * highlighted for as long as its note is open; any other passage's highlight
   * fades on its own after a few seconds.
   */
  const emphasizeItem = (item: PlaylistItemData | null) => {
    if (!tab || item?.type !== "bible-verse" || item.ref.verse == null) {
      return;
    }
    disposeDecoration();
    const { ref } = item;
    const loadedChapter = tab.readingState.chapterData.value;
    const chapterDataMatches =
      loadedChapter?.book.id === ref.bookId &&
      loadedChapter?.chapter.number === ref.chapter;
    const endVerse = ref.toEndOfChapter
      ? chapterDataMatches
        ? loadedChapter.numberOfVerses
        : undefined
      : ref.endVerse;

    decorationId = emphasizeVerses(
      tab.readingState,
      {
        book: ref.bookId as BookId,
        chapter: ref.chapter,
        verse: ref.verse,
        endChapter: ref.endChapter,
        endVerse: endVerse,
      },
      undefined,
      {
        // Every participant in a shared session highlights the step for
        // themselves, and each one's highlight follows their own note.
        localOnly: true,
        ...(hasScriptureNote(item)
          ? {
              removeAfterMs: noteExpanded.peek()
                ? null
                : NOTE_CLOSED_HIGHLIGHT_MS,
            }
          : {}),
      }
    );
  };

  /**
   * Opens or closes the current step's note. Closing it lets the passage's
   * highlight fade after {@link NOTE_CLOSED_HIGHLIGHT_MS}; opening it again
   * brings the highlight back.
   */
  const setNoteExpanded = (expanded: boolean): void => {
    if (noteExpanded.peek() === expanded) {
      return;
    }
    noteExpanded.value = expanded;
    const item = currentItem.peek();
    if (!hasScriptureNote(item)) {
      return;
    }
    if (expanded) {
      // While away at a linked passage the step's verses aren't on screen;
      // they're highlighted again on the way back.
      if (!awayFromStep.peek()) {
        emphasizeItem(item);
      }
    } else if (decorationId && !noteClosedTimer) {
      const closing = decorationId;
      noteClosedTimer = setTimeout(() => {
        noteClosedTimer = null;
        if (decorationId === closing) {
          disposeDecoration();
        }
      }, NOTE_CLOSED_HIGHLIGHT_MS);
    }
  };

  const navigateToCurrentItem = async () => {
    const navigation = ++navigationCount;
    const item = currentItem.value;
    detour.value = null;
    // Cleared even when the new step isn't scripture: a noted passage's
    // highlight has no timer of its own and would otherwise outlast its step.
    disposeDecoration();
    if (!tab || item?.type !== "bible-verse") {
      return;
    }
    hasShownStep = true;
    const { ref, translationId } = item;

    // `translationId` is optional on the item; fall back to the tab's current
    // translation. `.peek()` avoids re-navigating when the tab changes it.
    // `ref.chapter` is the start of a chapter range, so a whole-chapter item
    // and a "John 1-3" range both open the first chapter. Verse items also
    // scroll to the start verse; chapter items have no verse to highlight.
    await tab.readingState.selectTranslationAndChapter(
      translationId ?? tab.readingState.translationId.peek(),
      ref.bookId,
      ref.chapter,
      ref.verse != null ? { scrollToVerse: ref.verse } : undefined
    );
    if (navigation !== navigationCount) {
      return;
    }
    emphasizeItem(item);
  };

  /**
   * Moves to a step, ending any detour in the same update, so a shared
   * session never sees the new step paired with the old detour (which would
   * send everyone to that passage before the step's).
   */
  const moveToStep = (index: number): void => {
    batch(() => {
      currentIndex.value = index;
      detour.value = null;
      // Even onto an identical item, which the rearm effect can't tell apart.
      noteExpanded.value = true;
    });
  };

  /**
   * Takes the reader to a passage the current step's note links to, without
   * leaving the playlist: the step, its note and the queue stay as they are,
   * and `returnToStep` brings the reader back. The linked passage gets the
   * usual brief highlight.
   */
  const visitReference = async (ref: ReaderVerseRef): Promise<void> => {
    if (!tab) {
      return;
    }
    const navigation = ++navigationCount;
    // Only what locates the passage, so it syncs as plain, comparable JSON.
    const target: ReaderVerseRef = {
      book: ref.book,
      chapter: ref.chapter,
      ...(ref.verse != null ? { verse: ref.verse } : {}),
      ...(ref.endChapter != null ? { endChapter: ref.endChapter } : {}),
      ...(ref.endVerse != null ? { endVerse: ref.endVerse } : {}),
    };
    if (!jsonEqual(detour.peek(), target)) {
      detour.value = target;
    }
    disposeDecoration();
    await tab.readingState.selectTranslationAndChapter(
      tab.readingState.translationId.peek(),
      ref.book,
      ref.chapter,
      ref.verse != null ? { scrollToVerse: ref.verse } : undefined
    );
    // Gone elsewhere while the chapter was loading: to another passage or
    // step, or back from this one.
    if (navigation !== navigationCount || !awayFromStep.peek()) {
      return;
    }
    disposeDecoration();
    decorationId = emphasizeVerses(tab.readingState, target, undefined, {
      localOnly: true,
    });
  };

  /** Brings the reader back from a linked passage to the current step. */
  const returnToStep = (): Promise<void> => navigateToCurrentItem();

  /**
   * Advances to the next step. On the last item it calls `onFinish` when
   * `canFinish` allows, and is otherwise a no-op.
   */
  const next = async (): Promise<void> => {
    if (hasNext.value) {
      stepArrival = "forward";
      moveToStep(currentIndex.value + 1);
      await navigateToCurrentItem();
    } else if (canFinish.value) {
      finishPromptShown.value = true;
      onFinish?.();
    }
  };

  /** Goes back to the previous step. No-op at the start of the queue. */
  const previous = async (): Promise<void> => {
    if (hasPrevious.value) {
      stepArrival = "backward";
      moveToStep(currentIndex.value - 1);
      await navigateToCurrentItem();
    }
  };

  /** Jumps to the given index. No-op when the index is out of range. */
  const jumpTo = async (index: number): Promise<void> => {
    if (index >= 0 && index < queue.value.length) {
      // Landing on the step already showing isn't a new arrival. Callers
      // re-jump the current step to make the reader catch up (a shared link
      // does this), and that must not forget a forward opening.
      if (currentIndex.peek() !== index) {
        stepArrival = "direct";
      }
      moveToStep(index);
      await navigateToCurrentItem();
    }
  };

  /** Appends an item to the end of the queue. */
  const addToQueue = (item: PlaylistItemData): void => {
    queue.value = [...queue.value, ...expandCrossChapterItem(item)];
    if (currentIndex.value < 0) {
      currentIndex.value = 0;
    }
  };

  /**
   * Removes the item at the given index from the queue, keeping
   * `currentIndex` pointed at a sensible item. No-op when out of range.
   */
  const removeFromQueue = (index: number): void => {
    if (index < 0 || index >= queue.value.length) {
      return;
    }
    if (index === currentIndex.value) {
      // The step being shown is going away, so its highlight goes with it, as
      // does any detour its note's link started.
      disposeDecoration();
      detour.value = null;
    }
    const nextQueue = queue.value.filter((_, i) => i !== index);
    let nextIndex = currentIndex.value;
    if (index < nextIndex) {
      // A step before the current one was removed; shift to compensate.
      nextIndex -= 1;
    }
    // Together, so the step never briefly points at the wrong item.
    batch(() => {
      queue.value = nextQueue;
      currentIndex.value =
        nextQueue.length === 0
          ? -1
          : Math.max(0, Math.min(nextIndex, nextQueue.length - 1));
    });
  };

  /**
   * Moves an item within the queue, adjusting `currentIndex` so the
   * currently-playing item stays selected. No-op when either index is out of
   * range.
   */
  const reorderQueue = (from: number, to: number): void => {
    const length = queue.value.length;
    if (from < 0 || from >= length || to < 0 || to >= length || from === to) {
      return;
    }
    const nextQueue = [...queue.value];
    const [moved] = nextQueue.splice(from, 1);
    if (!moved) {
      return;
    }
    nextQueue.splice(to, 0, moved);

    const current = currentIndex.value;
    let nextIndex = current;
    if (current === from) {
      nextIndex = to;
    } else if (from < current && to >= current) {
      nextIndex = current - 1;
    } else if (from > current && to <= current) {
      nextIndex = current + 1;
    }
    // Together, so the step never briefly points at the wrong item.
    batch(() => {
      queue.value = nextQueue;
      currentIndex.value = nextIndex;
    });
  };

  /** Restarts playback from the first item. */
  const reset = async (): Promise<void> => {
    stepArrival = "direct";
    moveToStep(queue.value.length > 0 ? 0 : -1);
    await navigateToCurrentItem();
  };

  /**
   * Applies a full playback snapshot. Used by the playlist reading extension to
   * hydrate from and stay in sync with its serializable `data` (including a
   * queue changed by a session peer). Each field is written only when it
   * differs from the current value, so applying an inbound snapshot doesn't
   * re-trigger the outbound sync effect for unchanged fields.
   */
  // The chapter open kicked off by the latest `setState`. Callers that start
  // playback can await this so a loader stays up until the verse is actually
  // on screen, not merely until the extension has been enabled.
  let settled: Promise<void> = Promise.resolve();

  const setState = async (state: PlaylistReadingData): Promise<void> => {
    if (!jsonEqual(playlists.peek(), state.playlists)) {
      playlists.value = state.playlists;
    }
    const queueChanged = !jsonEqual(queue.peek(), state.queue);
    if (queueChanged) {
      queue.value = state.queue;
    }
    const clampedStep =
      state.queue.length > 0
        ? Math.min(Math.max(Math.floor(state.step), 0), state.queue.length - 1)
        : -1;
    const nextDetour = state.detour ?? null;
    const stepChanged = currentIndex.peek() !== clampedStep;
    const detourChanged = !jsonEqual(detour.peek(), nextDetour);
    // A snapshot (a peer, a URL, or a new playlist handed to the extension
    // already running) placed us on this step. The step number staying the
    // same is not enough to keep the old direction: the item here may be a
    // different one. Closing it stays put. An echo of this client's own queue
    // edit never lands here, because that edit updates `queue` before the
    // snapshot is written back.
    if (stepChanged || queueChanged) {
      stepArrival = "direct";
    }
    // Together, so the outbound sync never sees the new step without its
    // detour and publishes the detour as cleared.
    batch(() => {
      currentIndex.value = clampedStep;
      if (detourChanged) {
        detour.value = nextDetour;
      }
    });
    let navigation: Promise<void> | null = null;
    if (nextDetour) {
      // Following a peer to a passage a note links to — or our own detour
      // coming back around, in which case the reader is already there.
      if (stepChanged || detourChanged) {
        navigation = visitReference(nextDetour);
      }
    } else if (stepChanged || detourChanged) {
      navigation = navigateToCurrentItem();
    } else {
      // Check if navigation is needed to display the current reference
      const current = currentItem.peek();
      if (current?.type === "bible-verse" && tab?.readingState) {
        if (
          !hasShownStep ||
          current.ref.bookId !== tab.readingState.bookId.peek() ||
          current.ref.chapter !== tab.readingState.chapterNumber.peek()
        ) {
          navigation = navigateToCurrentItem();
        }
      }
    }
    if (navigation) {
      settled = navigation;
      await navigation;
    }
  };

  const whenSettled = (): Promise<void> => settled;

  /** Tears down the navigation effect. Call when playback ends or is replaced. */
  const dispose = (): void => {
    disposeRearm();
    disposeDecoration();
  };

  return {
    playlists,
    queue,
    currentIndex,
    currentItem,
    hasNext,
    hasPrevious,
    canFinish,
    canPressNext,
    finishPromptShown: finishPromptShown as ReadonlySignal<boolean>,
    noteExpanded: noteExpanded as ReadonlySignal<boolean>,
    setNoteExpanded,
    awayFromStep: awayFromStep as ReadonlySignal<boolean>,
    detour: detour as ReadonlySignal<ReaderVerseRef | null>,
    visitReference,
    returnToStep,
    tab,
    next,
    previous,
    jumpTo,
    addToQueue,
    removeFromQueue,
    reorderQueue,
    reset,
    setState,
    getStepArrival: () => stepArrival,
    setStepArrival: (arrival: PlaylistStepArrival) => {
      stepArrival = arrival;
    },
    whenSettled,
    dispose,
  };
}

/**
 * What playback a URL asks for: the playlist `locator` and the 0-based queue
 * `stepIndex` (null when it names none). Read from a playing playlist's path,
 * `/{lang}/playlist/{locator}/{title}/{step}` (whose step is 1-based), or
 * from the `?playlist=`/`?playlistStep=` params that ad-hoc queues (a reading
 * plan's day) still use and old links carried. Null when the URL asks for no
 * playback, including a playlist's own page without a step.
 */
export function playbackRequestFromUrl(
  url: URL,
  basePath: string
): { locator: string; stepIndex: number | null } | null {
  const page = parsePlaylistPagePath(url.pathname, basePath);
  if (page) {
    return page.step != null
      ? { locator: page.locator, stepIndex: page.step - 1 }
      : null;
  }
  const locator = url.searchParams.get("playlist");
  if (!locator) {
    return null;
  }
  const step = url.searchParams.get("playlistStep");
  return {
    locator,
    stepIndex: step === null ? null : Math.floor(parseNumber(step, 0)),
  };
}

const PLAYLIST_FINISHED_MODAL_ID = "playlist-finished";

/** Stable id so navigating between non-verse items updates the same modal instead of closing/reopening it. */
const PLAYLIST_ITEM_MODAL_ID = "playlist-item-content";

/**
 * Id of the reading extension that lets a reading state's own next/previous-
 * chapter controls (keyboard, swipe, toolbar) advance playlist playback
 * instead, and lets its URL query params include `playlist`/`playlistStep`.
 */
const PLAYLIST_READING_EXTENSION_ID = "playlist";

/** Id under which the playlist-editing tools are registered with `ChatsManager`. */
const PLAYLIST_EDITOR_CHAT_CONTEXT_ID = "playlist-editor";

export function createPlaylistManager(
  os: CasualOSManager,
  login: LoginManager,
  tabs: TabsManager,
  navigation: NavigationManager,
  isMobile: ReadonlySignal<boolean>,
  modals: ModalManager,
  i18n: I18nManager,
  readingExtensionManager: BibleReadingExtensionManager,
  discover: DiscoverManager,
  chats: ChatsManager,
  initialPlaylistPageSeed?: PlaylistPageSeed,
  options: {
    /**
     * Limits how many friends' reads run at once. Shared with the other
     * managers that read friends' content, so one limit covers them all.
     * Omitted means a limit of this manager's own.
     */
    friendReads?: FriendReadLimiter;
  } = {}
) {
  const initialPlaybackRequest = playbackRequestFromUrl(
    navigation.initialUrl,
    navigation.basePath
  );
  /**
   * The playlist the initial URL asked to play, until that load settles.
   * Bridges the gap before playback starts, so the URL sync doesn't take the
   * not-yet-playing state for a request to stop.
   */
  const initialPlaylistLocator = signal(
    initialPlaybackRequest?.locator ?? null
  );
  const userPlaylists = signal<Playlist[]>([]);
  /** Latest play per playlist for the signed-in user, newest first. */
  const userPlaylistHistory = signal<PlaylistPlayHistory[]>([]);

  // view/isDiscoverOpen are owned by DiscoverManager now; these are local
  // aliases so the rest of this function keeps working unchanged.
  const view = discover.view;
  const isDiscoverOpen = discover.isDiscoverOpen;

  /** The playlist currently being edited/created in the pane, or null. */
  const editingPlaylist = signal<Playlist | null>(null);
  /** Copy of the draft when the editor opened, for unsaved-change detection. */
  const editingPlaylistBaseline = signal<Playlist | null>(null);

  const linkPreviews = createLinkPreviewLoader((url) =>
    os.getLinkPreview(url, i18n.language.peek())
  );

  /**
   * Fetches a preview for a link item just saved into the edited playlist and
   * swaps it in. Matches by object identity, so an item edited or removed
   * while the fetch was in flight is left alone.
   */
  const requestEditingItemPreview = (item: PlaylistItemData) => {
    linkPreviews.request(item, (original, previewed) => {
      const current = editingPlaylist.peek();
      if (!current) {
        return;
      }
      editingPlaylist.value = {
        ...current,
        items: current.items.map((existing) =>
          existing === original ? previewed : existing
        ),
      };
    });
  };

  /**
   * Id of the history row being written for the active play session, or null
   * when nothing is being tracked (signed out, `history: false`, or idle).
   */
  const activePlayHistoryId = signal<string | null>(null);
  const activePlayHistoryStartedAtMs = signal<number | null>(null);

  /**
   * The reading state playback is bound to: the shared-session tab if one
   * exists (so session participants converge on it), otherwise the selected
   * tab. Every playback operation and the URL sync target THIS tab only, so
   * playback on other tabs is never disturbed — switching tabs just changes
   * which reading state the UI reflects, it never stops or starts playback.
   */
  const activeTab = computed<ReaderTab | null>(
    () =>
      tabs.tabs.value.find((tab) => tab.id === tabs.selectedTabId.value) ?? null
  );

  /**
   * The playback state shown in the UI, or null when the active tab isn't
   * playing. Derived (not stored): playback lives inside the active reading
   * state's playlist-extension enablement, so this reads the live
   * `PlayingState` off that runtime. Switching the selected tab recomputes
   * this — it does not tear anything down.
   */
  /**
   * True from the moment a playlist is asked to play until its first item is
   * on screen. Fetching the playlist and opening the chapter both count —
   * that gap is when the UI shows a loader.
   */
  const openingPlayback = signal(false);
  let openingGeneration = 0;

  const beginOpeningPlayback = () => {
    openingGeneration += 1;
    openingPlayback.value = true;
    return openingGeneration;
  };

  const finishOpeningPlayback = (generation: number) => {
    if (generation === openingGeneration) {
      openingPlayback.value = false;
    }
  };

  const playing = computed<PlayingState | null>(() => {
    const runtime = activeTab.value?.readingState.enabledExtensions.value.find(
      (r) => r.id === PLAYLIST_READING_EXTENSION_ID
    );
    return runtime
      ? (runtime.instance as PlaylistReadingExtensionInstance).playingState
      : null;
  });

  const actualView = computed(() =>
    discover.resolveActualView(!!playing.value)
  );

  const availablePlaylists = computed(() => {
    return userPlaylists;
  });

  // Opens the content modal for a non-verse item (video/link/text), or closes
  // it for verse items, which are shown in the reader. The effect below calls
  // this when the current item changes. Tapping the queue item you're already
  // on doesn't change the index, so that tap calls `showCurrentItem` instead.
  const showItemInModal = (playingState: PlayingState) => {
    const item = playingState.currentItem.value;
    if (!item || item.type === "bible-verse") {
      modals.closeModal(PLAYLIST_ITEM_MODAL_ID);
      return;
    }

    const moveAfterClose = (move: Promise<void>) => {
      move.catch((err) =>
        console.error("Failed to move playlist after closing an item:", err)
      );
    };

    openPlaylistItemPreview(
      modals,
      item,
      PLAYLIST_ITEM_MODAL_ID,
      i18n.t,
      () => {
        // Tied to the playback that opened this pop-up. Closing continues
        // the direction they were already moving. There is no next or
        // previous step at the ends, and closing must not finish the
        // playlist — that only happens when they press next themselves.
        const arrival = playingState.getStepArrival();
        const onFirstItem = playingState.currentIndex.peek() === 0;
        // The first item has nowhere to go back to, so closing it steps
        // forward. That includes pressing Play on your own playlist, which
        // opens the intro directly rather than by Next.
        if (
          playingState.hasNext.peek() &&
          (arrival === "forward" || onFirstItem)
        ) {
          moveAfterClose(playingState.next());
        } else if (arrival === "backward" && playingState.hasPrevious.peek()) {
          moveAfterClose(playingState.previous());
        }
      },
      playingState
    );
  };

  /** Reopens the pop-up for the item playback is already on. */
  const showCurrentItem = (): void => {
    const state = playing.peek();
    if (state) {
      showItemInModal(state);
    }
  };

  const savePlaylist = async (playlist: Playlist) => {
    await os.recordData(playlist.recordName, playlist.id, playlist, {
      marker: "publicRead:playlists",
    });
  };

  const savePlayHistory = async (entry: PlaylistPlayHistory) => {
    const parsed = PlaylistPlayHistorySchema.parse(entry);
    await os.recordData(parsed.recordName, parsed.id, parsed, {
      marker: PLAYLIST_PLAY_HISTORY_MARKER,
    });
  };

  const erasePlayHistory = async (
    entry: PlaylistPlayHistory
  ): Promise<void> => {
    try {
      await os.eraseData(entry.recordName, entry.id);
    } catch (error) {
      console.error("Failed to erase playlist play history:", error);
    }
  };

  /**
   * Drops every history row that is not in `keep`, both in memory (caller
   * updates the signal) and best-effort on the backend. Used after load and
   * after starting a play so leftover sessions for the same playlist (and
   * overflow past the retention cap) never accumulate.
   */
  const prunePlayHistoryOutside = (
    all: PlaylistPlayHistory[],
    keep: PlaylistPlayHistory[]
  ): void => {
    const keepIds = new Set(keep.map((entry) => entry.id));
    for (const entry of all) {
      if (!keepIds.has(entry.id)) {
        void erasePlayHistory(entry);
      }
    }
  };

  const loadPlayHistory = async (
    recordName: string
  ): Promise<PlaylistPlayHistory[]> => {
    // Paginate through every page — a single `listDataByMarker` page is ordered
    // by record address (random UUID), not by recency, so recent plays would
    // silently disappear once history grew past one page.
    const records = await os.listAllDataByMarker(
      recordName,
      PLAYLIST_PLAY_HISTORY_MARKER
    );
    if (records.success === false) {
      throw new Error("Failed to list playlist play history");
    }
    const all = records.items
      .map((record) => PlaylistPlayHistorySchema.safeParse(record.data))
      .filter((r) => r.success)
      .map((r) => r.data);
    const retained = retainPlaylistPlayHistory(all);
    prunePlayHistoryOutside(all, retained);
    return retained;
  };

  const upsertLocalPlayHistory = (entry: PlaylistPlayHistory) => {
    // `.peek()` so callers inside playback effects don't subscribe to the list
    // they are writing (that would trip Preact's cycle detection).
    const key = playlistPlayHistoryKey(entry);
    const without = userPlaylistHistory
      .peek()
      .filter((e) => e.id !== entry.id && playlistPlayHistoryKey(e) !== key);
    userPlaylistHistory.value = retainPlaylistPlayHistory([entry, ...without]);
  };

  /**
   * Writes progress for a history row. Skips the backend write when only the
   * wall-clock duration moved (idle parked on one step) — those are refreshed
   * locally for the Discover accordion and flushed for real on stop / step
   * change. Persistence is otherwise best-effort.
   */
  const persistPlayHistoryPatch = async (
    id: string,
    startedAtMs: number,
    patch: {
      currentStep: number;
      totalSteps: number;
      lastItem: PlaylistItemData | null;
      endedAtMs?: number | null;
    }
  ): Promise<void> => {
    const existing = userPlaylistHistory.peek().find((e) => e.id === id);
    if (!existing) {
      return;
    }
    const now = Date.now();
    // Playing the same playlist again reopens this row before a just-queued
    // finalize write lands; skip that stale "ended" patch so it cannot close
    // the session we just started.
    if (patch.endedAtMs != null && activePlayHistoryId.peek() === id) {
      return;
    }
    const endedAtMs =
      patch.endedAtMs !== undefined ? patch.endedAtMs : existing.endedAtMs;
    const durationMs = Math.max(0, (endedAtMs ?? now) - startedAtMs);
    const next: PlaylistPlayHistory = {
      ...existing,
      currentStep: patch.currentStep,
      totalSteps: patch.totalSteps,
      lastItem: patch.lastItem,
      endedAtMs: endedAtMs ?? null,
      durationMs,
      updatedAtMs: now,
    };

    const stepUnchanged =
      existing.currentStep === patch.currentStep &&
      existing.totalSteps === patch.totalSteps &&
      jsonEqual(existing.lastItem, patch.lastItem);
    const ending =
      patch.endedAtMs !== undefined && patch.endedAtMs !== existing.endedAtMs;

    // Always keep the in-memory row fresh for the accordion; only hit the
    // network when the user actually advanced or the session ended.
    upsertLocalPlayHistory(next);
    if (!ending && stepUnchanged) {
      return;
    }
    try {
      await savePlayHistory(next);
    } catch (error) {
      console.error("Failed to save playlist play history:", error);
    }
  };

  const patchActivePlayHistory = async (patch: {
    currentStep: number;
    totalSteps: number;
    lastItem: PlaylistItemData | null;
    endedAtMs?: number | null;
  }): Promise<void> => {
    const id = activePlayHistoryId.peek();
    const startedAtMs = activePlayHistoryStartedAtMs.peek();
    if (!id || startedAtMs == null) {
      return;
    }
    await persistPlayHistoryPatch(id, startedAtMs, patch);
  };

  /**
   * Opens (or resets) the single history row for this playlist. Playing again
   * overwrites that row with the current session so Discover shows latest
   * progress, not a log of every play. Local list updates first so Discover
   * can show it immediately; the CasualOS write is best-effort. Leftover
   * rows for the same playlist and overflow past the retention cap are erased.
   */
  const beginPlayHistory = async (
    playlist: SimplePlaylist & Pick<Playlist, "recordName">,
    queue: PlaylistItemData[],
    step: number
  ): Promise<void> => {
    const userId = login.userId.peek();
    if (!userId) {
      return;
    }
    const now = Date.now();
    const previous = userPlaylistHistory.peek();
    const existing = previous.find(
      (entry) =>
        playlistPlayHistoryKey(entry) ===
        playlistPlayHistoryKey({
          playlistRecordName: playlist.recordName,
          playlistId: playlist.id,
        })
    );
    const entry = PlaylistPlayHistorySchema.parse({
      id: existing?.id ?? `playlist_history_${uuid()}`,
      recordName: userId,
      userId,
      playlistId: playlist.id,
      playlistRecordName: playlist.recordName,
      playlistTitle: playlist.title,
      playlistDescription: playlist.description,
      playlistHeroImageUrl: playlist.heroImageUrl ?? null,
      previousHistoryId: null,
      totalSteps: queue.length,
      currentStep: step,
      lastItem: queue[step] ?? null,
      startedAtMs: now,
      endedAtMs: null,
      durationMs: 0,
      createdAtMs: existing?.createdAtMs ?? now,
      updatedAtMs: now,
    });
    activePlayHistoryId.value = entry.id;
    activePlayHistoryStartedAtMs.value = now;

    const nextList = retainPlaylistPlayHistory([entry, ...previous]);
    userPlaylistHistory.value = nextList;
    prunePlayHistoryOutside([entry, ...previous], nextList);

    try {
      await savePlayHistory(entry);
    } catch (error) {
      console.error("Failed to save playlist play history:", error);
    }
  };

  /**
   * Snapshots the current active history session (if any), clears the active
   * pointers immediately, then persists the ended row. Clearing first avoids
   * racing a follow-up `beginPlayHistory` that would otherwise see the old id.
   */
  const finalizeActivePlayHistory = (): void => {
    const id = activePlayHistoryId.peek();
    const startedAtMs = activePlayHistoryStartedAtMs.peek();
    const playingState = playing.peek();
    activePlayHistoryId.value = null;
    activePlayHistoryStartedAtMs.value = null;
    if (!id || startedAtMs == null) {
      return;
    }
    const step = playingState?.currentIndex.peek() ?? -1;
    const queue = playingState?.queue.peek() ?? [];
    void persistPlayHistoryPatch(id, startedAtMs, {
      currentStep: step,
      totalSteps: queue.length,
      lastItem: queue[step] ?? null,
      endedAtMs: Date.now(),
    });
  };

  const isHistoryForPlaylist = (
    entry: PlaylistPlayHistory,
    playlist: Pick<Playlist, "id" | "recordName">
  ): boolean =>
    entry.playlistId === playlist.id &&
    entry.playlistRecordName === playlist.recordName;

  /**
   * Drops matching history rows from memory and the backend. Clears the
   * active session pointer first when it points at a dropped row so an
   * in-flight finalize cannot resurrect it.
   */
  const dropPlayHistoryForPlaylist = (
    playlist: Pick<Playlist, "id" | "recordName">
  ): void => {
    const matching = userPlaylistHistory
      .peek()
      .filter((entry) => isHistoryForPlaylist(entry, playlist));
    if (matching.some((entry) => entry.id === activePlayHistoryId.peek())) {
      activePlayHistoryId.value = null;
      activePlayHistoryStartedAtMs.value = null;
    }
    userPlaylistHistory.value = userPlaylistHistory
      .peek()
      .filter((entry) => !isHistoryForPlaylist(entry, playlist));
    for (const entry of matching) {
      void erasePlayHistory(entry);
    }
  };

  /**
   * Removes one play session from Discover history. Playback itself is left
   * running if this was the active row; only tracking stops.
   */
  const removePlayHistory = async (
    entry: PlaylistPlayHistory
  ): Promise<void> => {
    if (activePlayHistoryId.peek() === entry.id) {
      activePlayHistoryId.value = null;
      activePlayHistoryStartedAtMs.value = null;
    }
    userPlaylistHistory.value = userPlaylistHistory
      .peek()
      .filter((existing) => existing.id !== entry.id);
    await erasePlayHistory(entry);
  };

  /**
   * Reports a playlist analytics event for each given playlist, tagging
   * whether the current user authored it. Used to distinguish playback by the
   * creator from playback by someone the playlist was shared with. Accepts
   * `SimplePlaylist` (an unsaved playlist, e.g. an AI-generated one played
   * before it's ever recorded) as well as a full `Playlist` — an unsaved
   * playlist has no `authorUserId` on record, so it's attributed to the
   * current user, the only one who could have been playing it.
   * `extraProperties` are merged in on top of the common `playlistId`/
   * `playlistLocator`/`isCreator` fields, so every playlist event stays
   * queryable by the same base shape in PostHog.
   */
  const capturePlaylistEvent = (
    eventName: string,
    playlists: SimplePlaylist[],
    extraProperties: Record<string, unknown> = {}
  ): void => {
    if (typeof posthog === "undefined" || !posthog) {
      return;
    }
    const userId = login.userId.peek();
    for (const playlist of playlists) {
      const authorUserId = playlist.authorUserId;
      posthog.capture(eventName, {
        playlistId: playlist.id,
        playlistLocator: getPlaylistLocator(playlist),
        isCreator: authorUserId == null || authorUserId === userId,
        ...extraProperties,
      });
    }
  };

  /**
   * Permanently deletes a playlist: erases its record, drops it from
   * `userPlaylists`, clears matching play-history rows, and clears any
   * edit/playback state that referenced it.
   */
  const deletePlaylist = async (playlist: Playlist): Promise<void> => {
    const result = await os.eraseData(playlist.recordName, playlist.id);
    if (!result.success) {
      console.error("Failed to delete playlist:", result);
      throw new Error(`Failed to delete playlist: ${result.errorCode}`);
    }
    userPlaylists.value = userPlaylists.value.filter(
      (p) => p.id !== playlist.id
    );
    if (editingPlaylist.peek()?.id === playlist.id) {
      cancelEditingPlaylist();
    }
    const wasPlaying = !!playing
      .peek()
      ?.playlists.peek()
      .some((p) => p.id === playlist.id);
    // Drop history before stopPlaying so finalize cannot write the rows back.
    dropPlayHistoryForPlaylist(playlist);
    if (wasPlaying) {
      stopPlaying();
    }
  };

  const listPlaylists = async (recordName: string, onPage?: () => void) => {
    // Every page: the server's paging stores return ten records at a time.
    const records = await listAllByAddress(
      (lastAddress) =>
        os.listDataByMarker(recordName, "publicRead:playlists", lastAddress),
      onPage
    );
    return records.items.map((record) => PlaylistSchema.parse(record.data));
  };

  // Cached playlists for an explicitly-named account, keyed by userId. Only
  // ever populated via `getUserPlaylists` (never for the signed-in user's own
  // list, which lives in `userPlaylists` above), so there's no risk of one
  // account's data leaking into another's view on a sign-in switch — unlike
  // `AnnotationsManager`/`HighlightsManager`, this cache never needs an
  // `explicit`-flag/sweep pair to protect it.
  type UserPlaylistsEntry = {
    data: Signal<Playlist[]>;
    settled: boolean;
    load: Promise<void> | null;
    /** When the last successful read finished. */
    loadedAtMs: number | null;
    /** The last read failed, so coming back to it reads again right away. */
    loadFailed: boolean;
  };
  const friendFreshness = createFriendContentFreshness(options.friendReads);
  const userPlaylistEntries = new Map<string, UserPlaylistsEntry>();
  // Identity-stable views handed to callers, keyed by userId. Never pruned:
  // evicting one would mint a new computed on the next call, breaking
  // identity for callers still holding the old one.
  const userPlaylistViews = new Map<string, ReadonlySignal<Playlist[]>>();

  const getOrCreateUserPlaylistsEntry = (
    userId: string
  ): UserPlaylistsEntry => {
    let entry = userPlaylistEntries.get(userId);
    if (!entry) {
      const created: UserPlaylistsEntry = {
        data: friendFreshness.trackedSignal<Playlist[]>([], () =>
          refreshUserPlaylists(userId, created)
        ),
        settled: false,
        load: null,
        loadedAtMs: null,
        loadFailed: false,
      };
      entry = created;
      userPlaylistEntries.set(userId, entry);
    }
    return entry;
  };

  const loadUserPlaylists = async (
    userId: string,
    entry: UserPlaylistsEntry
  ): Promise<void> => {
    try {
      const loaded = await friendFreshness.read(entry.data, (progress) =>
        listPlaylists(userId, progress)
      );
      // A load that settled the entry while this request was in the air
      // holds newer playlists than this response does.
      if (entry.settled) {
        return;
      }
      entry.data.value = loaded;
      entry.loadedAtMs = Date.now();
      entry.loadFailed = false;
      entry.settled = true;
    } catch (error) {
      if (!(error instanceof SkippedFriendRead)) {
        console.error(`Failed to load playlists for ${userId}:`, error);
      }
      if (!entry.settled) {
        entry.loadFailed = true;
        // A failed re-read keeps the playlists already shown.
        if (entry.loadedAtMs === null) {
          entry.data.value = [];
        }
        entry.settled = true;
      }
    }
  };

  /**
   * Reads a friend's playlists again when they're back on screen or the app
   * regains focus (see `createFriendContentFreshness`), keeping the ones
   * already shown until the new list arrives. A read that failed is tried
   * again then too, however recent it was.
   */
  const refreshUserPlaylists = (
    userId: string,
    entry: UserPlaylistsEntry
  ): void => {
    if (
      entry.load ||
      !(entry.loadFailed || isFriendContentStale(entry.loadedAtMs))
    ) {
      return;
    }
    entry.settled = false;
    void ensureUserPlaylistsLoaded(userId, entry);
  };

  const ensureUserPlaylistsLoaded = (
    userId: string,
    entry: UserPlaylistsEntry
  ): Promise<void> | null => {
    if (entry.settled) {
      return entry.load;
    }
    if (!entry.load) {
      entry.load = loadUserPlaylists(userId, entry).finally(() => {
        entry.load = null;
      });
    }
    return entry.load;
  };

  /**
   * Reactive view of one account's playlists, pinned to the account passed
   * in rather than whoever is signed in — this is how a friend's
   * playlists are read. Playlists are stored world-readable
   * (`publicRead:playlists`), so this works for any account and does not
   * require being signed in. Loads lazily on first access, keyed by account.
   */
  const getUserPlaylists = (userId: string): ReadonlySignal<Playlist[]> => {
    let view = userPlaylistViews.get(userId);
    if (!view) {
      view = computed(() => {
        const entry = getOrCreateUserPlaylistsEntry(userId);
        void ensureUserPlaylistsLoaded(userId, entry);
        return entry.data.value;
      });
      userPlaylistViews.set(userId, view);
    }

    // Kick the load eagerly so callers see fresh data as soon as possible,
    // without subscribing this call site to anything (the view itself is
    // what a caller should read to react to it arriving).
    void ensureUserPlaylistsLoaded(
      userId,
      getOrCreateUserPlaylistsEntry(userId)
    );

    return view;
  };

  /**
   * Loads a single playlist by its `{recordName, id}` locator. Used to open a
   * playlist that isn't the current user's own — e.g. from a shared `?playlist=`
   * link. Playlists are stored with the `publicRead:playlists` marker, so this
   * succeeds without being signed in.
   */
  const loadPlaylist = async (
    recordName: string,
    id: string
  ): Promise<Playlist> => {
    const result = await os.getData(recordName, id);
    if (!result.success) {
      throw new Error(`Failed to load playlist: ${result.errorCode}`);
    }
    return PlaylistSchema.parse(result.data);
  };

  /**
   * Starts creating a new playlist: opens the create view and sets
   * `editingPlaylist` to a fresh, unsaved playlist, optionally pre-filled
   * with the given title/description/items (e.g. an AI-generated playlist).
   * Persisting happens later via `saveEditingPlaylist`. No-op when not
   * signed in.
   */
  const createNewPlaylist = async (
    initial?: Pick<Playlist, "title" | "description" | "items">
  ): Promise<Signal<Playlist | null> | null> => {
    let userId = login.userId.value;
    if (!userId) {
      const userInfo = await login.login();
      if (!userInfo) {
        console.warn("Cannot create a playlist while signed out.");
        return null;
      }
      userId = userInfo.id;
    }
    const now = Date.now();
    const draft = PlaylistSchema.parse({
      id: `playlist_${uuid()}`,
      recordName: userId,
      authorUserId: userId,
      title: initial?.title ?? null,
      description: initial?.description ?? null,
      heroImageUrl: null,
      items: initial?.items ?? [],
      createdAtMs: now,
      updatedAtMs: now,
    });
    linkPreviews.cancel();
    editingPlaylist.value = draft;
    editingPlaylistBaseline.value = clonePlaylist(draft);
    view.value = "create_playlist";
    draft.items.forEach(requestEditingItemPreview);

    return editingPlaylist;
  };

  /**
   * Opens an existing playlist for editing: sets `editingPlaylist` to a copy of
   * the given playlist and switches to the create/edit view. Persisting happens
   * later via `saveEditingPlaylist`.
   */
  const editPlaylist = (playlist: Playlist): void => {
    linkPreviews.cancel();
    const draft = clonePlaylist(playlist);
    editingPlaylist.value = draft;
    editingPlaylistBaseline.value = clonePlaylist(playlist);
    view.value = "create_playlist";
    // Links saved before previews existed, or whose preview missed the save
    // cutoff, get one now so the next save stores it.
    draft.items.forEach(requestEditingItemPreview);
  };

  /**
   * Persists the currently-edited playlist, upserts it into `userPlaylists`,
   * then clears the editor and returns to the discover view. No-op when there
   * is no playlist being edited.
   */
  const saveEditingPlaylist = async (): Promise<void> => {
    await linkPreviews.settle();
    linkPreviews.cancel();
    const current = editingPlaylist.value;
    if (!current) {
      return;
    }
    const playlist: Playlist = { ...current, updatedAtMs: Date.now() };
    await savePlaylist(playlist);
    const exists = userPlaylists.value.some((p) => p.id === playlist.id);
    capturePlaylistEvent(
      exists ? "playlist_updated" : "playlist_created",
      [playlist],
      { itemCount: playlist.items.length }
    );
    userPlaylists.value = exists
      ? userPlaylists.value.map((p) => (p.id === playlist.id ? playlist : p))
      : [...userPlaylists.value, playlist];
    editingPlaylist.value = null;
    editingPlaylistBaseline.value = null;
    view.value = "discover";
  };

  /**
   * Patches the currently-edited playlist's title, description, and/or cover
   * image. No-op when there is no playlist being edited. Persisting happens
   * later via `saveEditingPlaylist`.
   */
  const updateEditingPlaylistMetadata = (
    updates: Partial<Pick<Playlist, "title" | "description" | "heroImageUrl">>
  ): string => {
    const current = editingPlaylist.value;
    if (!current) {
      return "error: no playlist is currently being edited";
    }
    editingPlaylist.value = { ...current, ...updates };
    return "success";
  };

  /**
   * Takes a cover image off every playlist of the user's that shows it, for
   * when the image is being deleted from their gallery — the covers would
   * otherwise point at a file that no longer exists. Each affected playlist is
   * saved without its cover; the ones that save are updated locally even if
   * another fails, and a failure is then re-thrown so the caller knows the
   * image is still in use somewhere. Returns the playlists that were changed.
   */
  const clearHeroImage = async (url: string): Promise<Playlist[]> => {
    const affected = userPlaylists.value.filter((p) => p.heroImageUrl === url);
    const results = await Promise.allSettled(
      affected.map(async (playlist) => {
        const next: Playlist = {
          ...playlist,
          heroImageUrl: null,
          updatedAtMs: Date.now(),
        };
        await savePlaylist(next);
        return next;
      })
    );
    const updated = results.flatMap((result) =>
      result.status === "fulfilled" ? [result.value] : []
    );
    if (updated.length > 0) {
      const byId = new Map(updated.map((p) => [p.id, p]));
      userPlaylists.value = userPlaylists.value.map((p) => byId.get(p.id) ?? p);
    }
    // The editor's copy loses the cover too, or saving the edit would put the
    // dead URL straight back. Its baseline follows so this alone does not
    // register as an unsaved change.
    const editing = editingPlaylist.peek();
    if (editing?.heroImageUrl === url) {
      editingPlaylist.value = { ...editing, heroImageUrl: null };
    }
    const baseline = editingPlaylistBaseline.peek();
    if (baseline?.heroImageUrl === url) {
      editingPlaylistBaseline.value = { ...baseline, heroImageUrl: null };
    }
    const failed = results.find(
      (result): result is PromiseRejectedResult => result.status === "rejected"
    );
    if (failed) {
      console.error("Failed to remove a playlist cover image:", failed.reason);
      throw new Error("Failed to remove the cover image from every playlist");
    }
    return updated;
  };

  /**
   * Appends an item to the currently-edited playlist. No-op when there is no
   * playlist being edited. Persisting happens later via `saveEditingPlaylist`.
   */
  const addEditingPlaylistItem = (item: PlaylistItemData): string => {
    const current = editingPlaylist.value;
    if (!current) {
      return "error: no editing playlist";
    }
    editingPlaylist.value = {
      ...current,
      items: [...current.items, item],
    };
    requestEditingItemPreview(item);
    return "success";
  };

  /**
   * Inserts an item at the given index in the currently-edited playlist. No-op when there is no
   * playlist being edited. Persisting happens later via `saveEditingPlaylist`.
   */
  const insertEditingPlaylistItem = (
    index: number,
    item: PlaylistItemData
  ): string => {
    const current = editingPlaylist.value;
    if (!current) {
      return "error: no editing playlist";
    }
    if (index < 0 || index > current.items.length) {
      return `error: index out of range (0-${current.items.length})`;
    }
    editingPlaylist.value = {
      ...current,
      items: [
        ...current.items.slice(0, index),
        item,
        ...current.items.slice(index),
      ],
    };
    requestEditingItemPreview(item);
    return "success";
  };

  /**
   * Replaces the item at the given index in the currently-edited playlist.
   * No-op when there is no playlist being edited or the index is out of range.
   * Persisting happens later via `saveEditingPlaylist`.
   */
  const updateEditingPlaylistItem = (
    index: number,
    item: PlaylistItemData
  ): string => {
    const current = editingPlaylist.value;
    if (!current) {
      return "error: no editing playlist";
    }
    if (index < 0 || index >= current.items.length) {
      return `error: index out of range (0-${current.items.length - 1})`;
    }
    const updated = carryOverLinkPreview(current.items[index], item);
    editingPlaylist.value = {
      ...current,
      items: current.items.map((existing, i) =>
        i === index ? updated : existing
      ),
    };
    requestEditingItemPreview(updated);
    return "success";
  };

  /**
   * Removes the item at the given index from the currently-edited playlist.
   * No-op when there is no playlist being edited. Persisting happens later via
   * `saveEditingPlaylist`.
   */
  const removeEditingPlaylistItem = (index: number): string => {
    const current = editingPlaylist.value;
    if (!current) {
      return "error: no editing playlist";
    }
    editingPlaylist.value = {
      ...current,
      items: current.items.filter((_, i) => i !== index),
    };
    return "success";
  };

  /**
   * Moves an item within the currently-edited playlist from one index to
   * another. No-op when there is no playlist being edited, either index is
   * out of range, or the indices are equal. Mirrors `reorderQueue`'s splice
   * semantics; unlike `reorderQueue` there's no `currentIndex`-style value to
   * follow along here — the caller is responsible for keeping any "currently
   * being edited" index pointed at the same logical item.
   */
  const reorderEditingPlaylistItem = (from: number, to: number): string => {
    const current = editingPlaylist.value;
    if (!current) {
      return "error: no editing playlist";
    }
    const length = current.items.length;
    if (from < 0 || from >= length) {
      return `error: original index out of range (0-${length - 1}) or equal`;
    } else if (to < 0 || to >= length) {
      return `error: target index out of range (0-${length - 1}) or equal`;
    } else if (from === to) {
      return "success";
    }
    const nextItems = [...current.items];
    const [moved] = nextItems.splice(from, 1);
    if (!moved) {
      return "success";
    }
    nextItems.splice(to, 0, moved);
    editingPlaylist.value = { ...current, items: nextItems };
    return "success";
  };

  /** Discards the current edit and returns to the discover view. */
  const cancelEditingPlaylist = (): void => {
    linkPreviews.cancel();
    editingPlaylist.value = null;
    editingPlaylistBaseline.value = null;
    view.value = "discover";
  };

  /**
   * True when the open draft's name, description, cover, or items differ from
   * what they were when the editor opened.
   */
  const isEditingPlaylistDirty = (): boolean => {
    const current = editingPlaylist.peek();
    const baseline = editingPlaylistBaseline.peek();
    if (!current || !baseline) {
      return false;
    }
    return playlistEditorState(current) !== playlistEditorState(baseline);
  };

  /**
   * Uploads a cover image to the current user's record and returns its public
   * URL. The caller is responsible for attaching that URL to the playlist
   * (typically via `updateEditingPlaylistMetadata`). Throws when signed out.
   */
  const uploadHeroImage = async (file: File): Promise<string> => {
    const userId = login.userId.value;
    if (!userId) {
      throw new Error("Cannot upload a cover image while signed out.");
    }
    return (await savePhotoToGallery(os, userId, file)).url;
  };

  /**
   * Starts playing the given playlist(s) at `initialStep`, by enabling the
   * playlist reading extension on the target tab (a shared-session tab if there
   * is one, otherwise the selected tab) with the playback state as its data. A
   * fresh queue is built from a copy of the playlists' items so it can be
   * reordered/edited without mutating the playlists. The extension owns the live
   * playing state; this manager reads it back via the `playing` computed.
   *
   * When the user is signed in and `options.history` is not `false`, the
   * single history row for the first recorded playlist is opened or reset.
   *
   * Returns the live playing state, or null when there is no tab to play on.
   */
  const startPlaying = (
    playlist: SimplePlaylist | SimplePlaylist[],
    initialStep = 0,
    options?: StartPlayingOptions
  ): PlayingState | null => {
    const playlists = Array.isArray(playlist) ? playlist : [playlist];
    // Play on the active tab only. Other tabs keep their own playback (if any)
    // untouched, so playback is isolated per reading state.
    const targetTab = activeTab.value;

    const queue = playlists.flatMap((p) =>
      p.items.flatMap(expandCrossChapterItem)
    );
    const step =
      queue.length > 0
        ? Math.min(Math.max(Math.floor(initialStep), 0), queue.length - 1)
        : -1;

    const openingItem = queue[0];
    // A shared opening on text or a link records that first close as forward.
    // In-app play records a direct landing; closing the first item still
    // steps forward. A shared opening on any later step is a direct landing.
    //
    // Reloads, restored tabs, and URL sync all come through
    // `startPlayingLocator`, which passes `sharedOpening`. The address bar
    // can't tell a link someone shared from a reload of your own playlist, so
    // a step-1 URL always walks the intro. Closing the first text item after
    // that reload moves on.
    const walkOpeningText =
      options?.sharedOpening === true &&
      step === 0 &&
      !!openingItem &&
      openingItem.type !== "bible-verse";

    // Ending an existing tracked session before opening the next one keeps
    // duration/endedAt accurate when the user starts another playlist without
    // an explicit stop.
    if (activePlayHistoryId.peek()) {
      finalizeActivePlayHistory();
    }
    capturePlaylistEvent("playlist_played", playlists);

    targetTab?.readingState.enableExtension(PLAYLIST_READING_EXTENSION_ID, {
      playlists,
      queue,
      step,
    } satisfies PlaylistReadingData);
    // Enabling an extension that is already running hands it this data via
    // `setState`, which only resets the direction when the step or the queue
    // changes. Always set it here so a second playlist that starts on the
    // same step doesn't keep the first playlist's direction.
    playing.peek()?.setStepArrival(walkOpeningText ? "forward" : "direct");
    const generation = beginOpeningPlayback();
    const opened = playing.peek();
    void (opened?.whenSettled() ?? Promise.resolve()).finally(() => {
      finishOpeningPlayback(generation);
    });
    view.value = "play_playlist";

    if (isMobile.value) {
      // on mobile, close the discover pane so the reader is visible while playing
      view.value = null;
    }

    const trackHistory = options?.history !== false;
    const firstPlaylist = playlists[0];
    if (
      trackHistory &&
      firstPlaylist &&
      isRecordedPlaylist(firstPlaylist) &&
      login.userId.peek()
    ) {
      void beginPlayHistory(firstPlaylist, queue, step);
    }

    return playing.value;
  };

  /**
   * Loads a playlist by locator and plays it at `stepIndex` (0-based). Reuses
   * the playlist a playlist page already loaded for the same locator. Resolves
   * once the reader has moved to that step, so the server can render it.
   */
  const startPlayingLocator = async (
    locator: string,
    stepIndex: number | null
  ): Promise<void> => {
    const parsed = parseRecordLocator(locator);
    if (!parsed) {
      console.error("Invalid playlist locator in URL:", locator);
      return;
    }
    const { recordName, address: id } = parsed;
    try {
      const loaded = playlistPageLoader.page.peek();
      const playlist =
        loaded?.locator === locator
          ? loaded.item
          : await loadPlaylist(recordName, id);
      const state = startPlaying(playlist, stepIndex ?? 0, {
        sharedOpening: true,
      });
      if (state) {
        await state.jumpTo(state.currentIndex.peek());
      }
    } catch (err) {
      console.error("Failed to load playlist for playback:", err);
    }
  };

  const openPlaylistFinishedModal = (
    playlist: Pick<Playlist, "id" | "recordName" | "title">
  ): void => {
    modals.closeModal(PLAYLIST_ITEM_MODAL_ID);
    modals.openModal({
      id: PLAYLIST_FINISHED_MODAL_ID,
      title: { key: "playlist-finished", defaultValue: "Playlist finished" },
      content: () => (
        <PlaylistFinishedModalContent
          playlistTitle={
            playlist.title ??
            i18n.t("untitled-playlist", { defaultValue: "Untitled playlist" })
          }
          shareUrl={getPlaylistUrl(playlist)}
          onClose={() => {
            stopPlaying();
            modals.closeModal(PLAYLIST_FINISHED_MODAL_ID);
          }}
        />
      ),
    });
  };

  /**
   * Gets a shareable URL for the given playlist: its own
   * `/{lang}/playlist/{locator}/{title}` page, which shows the playlist before
   * anything starts playing.
   */
  const getPlaylistUrl = (
    playlist: Pick<Playlist, "id" | "recordName" | "title">
  ): string =>
    new URL(
      `${navigation.basePath}${buildSharedPagePath({
        kind: "playlist",
        language: i18n.language.peek(),
        locator: getPlaylistLocator(playlist),
        title: playlist.title,
      })}`,
      navigation.currentUrl.value
    ).toString();

  const goBackFromPlayingView = () => {
    if (isMobile.value) {
      view.value = null;
    } else {
      stopPlaying();
    }
  };

  /**
   * Stops the active tab's playback and returns to the discover view. Only the
   * active reading state is disabled (disposing its live playing state, and in
   * a shared session propagating the stop to participants) — other tabs'
   * playback is left running.
   */
  const stopPlaying = (): void => {
    finalizeActivePlayHistory();
    const tab = activeTab.peek();
    if (tab?.readingState.isExtensionEnabled(PLAYLIST_READING_EXTENSION_ID)) {
      tab.readingState.disableExtension(PLAYLIST_READING_EXTENSION_ID);
    }
    initialPlaylistLocator.value = null;
    // Drop the loader. A later open bumps the generation itself, so a finish
    // from this one can only set the flag false, which this already did.
    openingPlayback.value = false;
    // A playing path only means anything while playing. Left in the address
    // bar, it would be read straight back as a request to play again.
    if (
      parsePlaylistPagePath(
        navigation.currentUrl.peek().pathname,
        navigation.basePath
      )?.step != null
    ) {
      tabs.leaveStaticPage();
    }
    modals.closeModal(PLAYLIST_ITEM_MODAL_ID);
    if (view.peek()) {
      view.value = "discover";
    }
  };

  /**
   * Loads the playlist for a history row and starts playback at the saved step.
   * The playlist's history row is reset to this new session.
   */
  const continueFromHistory = async (
    entry: PlaylistPlayHistory
  ): Promise<void> => {
    const generation = beginOpeningPlayback();
    try {
      const playlist = await loadPlaylist(
        entry.playlistRecordName,
        entry.playlistId
      );
      const step = Math.max(0, entry.currentStep);
      const playingState = startPlaying(playlist, step);
      if (!playingState) {
        throw new Error("Cannot play a playlist without an open tab.");
      }
      await playingState.whenSettled();
    } catch (error) {
      finishOpeningPlayback(generation);
      throw error;
    }
  };

  /**
   * Loads the playlist for a history row and starts playback from the beginning.
   * The playlist's history row is reset to this new session.
   */
  const replayFromHistory = async (
    entry: PlaylistPlayHistory
  ): Promise<void> => {
    const generation = beginOpeningPlayback();
    try {
      const playlist = await loadPlaylist(
        entry.playlistRecordName,
        entry.playlistId
      );
      const playingState = startPlaying(playlist, 0);
      if (!playingState) {
        throw new Error("Cannot play a playlist without an open tab.");
      }
      await playingState.whenSettled();
    } catch (error) {
      finishOpeningPlayback(generation);
      throw error;
    }
  };

  /**
   * Continues a history row, or replays it when that session already finished.
   * Resolves once the first item is on screen.
   */
  const playFromHistory = (entry: PlaylistPlayHistory): Promise<void> =>
    isPlaylistPlayHistoryComplete(entry)
      ? replayFromHistory(entry)
      : continueFromHistory(entry);

  const syncPlaylists = async () => {
    if (!login.userId.value) {
      userPlaylists.value = [];
      return;
    }

    try {
      const playlists = await listPlaylists(login.userId.value);
      userPlaylists.value = playlists;
    } catch (error) {
      console.error("Failed to sync playlists:", error);
    }
  };

  const syncPlayHistory = async () => {
    if (!login.userId.value) {
      userPlaylistHistory.value = [];
      activePlayHistoryId.value = null;
      activePlayHistoryStartedAtMs.value = null;
      return;
    }

    try {
      userPlaylistHistory.value = await loadPlayHistory(login.userId.value);
    } catch (error) {
      console.error("Failed to sync playlist play history:", error);
    }
  };

  effect(() => {
    void syncPlaylists();
  });

  effect(() => {
    void syncPlayHistory();
  });

  effect(() => {
    if (!playing.value) {
      return;
    }

    showItemInModal(playing.value);
  });

  // Mirror queue position into the active history row whenever it changes.
  // Identical step patches are no-ops for the backend (see persistPlayHistoryPatch),
  // so the write that beginPlayHistory already did is not immediately repeated.
  effect(() => {
    const historyId = activePlayHistoryId.value;
    const playingState = playing.value;
    if (!historyId || !playingState) {
      return;
    }
    const step = playingState.currentIndex.value;
    const queue = playingState.queue.value;
    void patchActivePlayHistory({
      currentStep: step,
      totalSteps: queue.length,
      lastItem: queue[step] ?? null,
    });
  });

  // Duration for an active session is refreshed locally on step changes and
  // flushed to the backend on stop — no idle 5s recordData timer.

  // Registered before the deep-link autoplay check below, since that check can
  // synchronously reach `startPlaying` -> `enableExtension`.
  //
  // Each enablement owns its own live playing state, built here from the
  // serializable `data` the enablement was given. Because `SessionsManager`
  // mirrors that `data` across a shared session, playback (queue + position)
  // stays in sync between participants.
  readingExtensionManager.registerReadingExtension({
    id: PLAYLIST_READING_EXTENSION_ID,
    activate: (ctx): ReadingExtensionInstance => {
      const { readingState } = ctx;
      // The registry is non-generic (`TData = unknown`); this extension owns the
      // shape it stores, so narrow the data signal to it here.
      const data = ctx.data as Signal<PlaylistReadingData | undefined>;
      const initial: PlaylistReadingData = data.peek() ?? {
        playlists: [],
        queue: [],
        step: 0,
      };

      // The tab this reading state belongs to, so bible-verse items navigate it.
      const tab =
        tabs.tabs.value.find((t) => t.readingState === readingState) ?? null;

      const playingState = createPlayingState(initial.playlists, tab, () => {
        const playlist = playingState.playlists.peek()[0];
        if (playlist && isRecordedPlaylist(playlist)) {
          openPlaylistFinishedModal(playlist);
        }
      });
      // Apply the synced queue + position (a peer's queue may differ from the
      // raw playlist items after add/remove/reorder).
      playingState.setState(initial);

      // Outbound: mirror local playback changes (advance, queue edits) into the
      // serializable `data` so they propagate to session participants. Guarded
      // by a structural compare so it never fights the inbound effect.
      const disposeOut = effect(() => {
        const snapshot: PlaylistReadingData = {
          playlists: playingState.playlists.value,
          queue: playingState.queue.value,
          step: playingState.currentIndex.value,
          detour: playingState.detour.value,
        };
        if (!jsonEqual(data.peek(), snapshot)) {
          data.value = snapshot;
        }
      });

      // Inbound: apply remote `data` changes onto the live playing state.
      // `setState` no-ops per field when unchanged, so this settles without
      // ping-ponging with the outbound effect.
      const disposeIn = effect(() => {
        const next = data.value;
        if (next) {
          // Untracked: applying the state reads the playing state's own
          // signals, and depending on them would re-apply this (by then
          // stale) data whenever playback moved, undoing the move.
          untracked(() => void playingState.setState(next));
        }
      });

      // A saved playlist's path carries the step. A scripture step moves the
      // reader, and that navigation writes the URL; a text or link step
      // doesn't, so it asks for the URL write itself.
      let lastIndex = playingState.currentIndex.peek();
      const disposeStepUrl = effect(() => {
        const index = playingState.currentIndex.value;
        if (index === lastIndex) {
          return;
        }
        lastIndex = index;
        const item = playingState.currentItem.peek();
        if (item && item.type !== "bible-verse") {
          readingState.requestUrlUpdate();
        }
      });

      // Reports a `playlist_finished` event the first time *this client's
      // own* forward navigation reaches the last item in the queue. Hooked
      // onto `next()` itself rather than a reactive effect on `currentIndex`,
      // because `next()` is only ever called by this client's own advance —
      // never by an inbound session sync (which moves the index via
      // `setState` instead, so every synced participant's index can shift
      // without each of them having "finished" anything) and never by a
      // queue edit (`removeFromQueue` clamping the index after the trailing
      // items are deleted doesn't call `next()` either). Comparing the index
      // before/after also rules out a single-item queue's starting position,
      // a deep link that opens directly on the last step, and a redundant
      // press of "next" while already on the last item — none of those are a
      // forward move. Guarded by `hasFiredFinished` so navigating back and
      // forth over the last item doesn't re-report it.
      let hasFiredFinished = false;
      const localNext = playingState.next;
      playingState.next = async (): Promise<void> => {
        const before = playingState.currentIndex.peek();
        await localNext();
        const after = playingState.currentIndex.peek();
        const queueLength = playingState.queue.peek().length;
        if (
          hasFiredFinished ||
          after <= before ||
          queueLength === 0 ||
          after !== queueLength - 1
        ) {
          return;
        }
        hasFiredFinished = true;
        capturePlaylistEvent(
          "playlist_finished",
          playingState.playlists.peek()
        );
      };

      // Playback governs stepping *within* the queue; at its edges the reader's
      // own chapter navigation takes over again. Before this, reaching the last
      // item left next/previous permanently dead — nothing stops playback from
      // the reading plans pane (its player lives in the discover pane, which
      // isn't even open), so the reader simply looked broken with no way out.
      const hasNext = computed(
        () =>
          playingState.hasNext.value ||
          (playingState.canFinish.value &&
            !playingState.finishPromptShown.value) ||
          !!readingState.chapterData.value?.nextChapterApiLink
      );
      const hasPrevious = computed(
        () =>
          playingState.hasPrevious.value ||
          !!readingState.chapterData.value?.previousChapterApiLink
      );

      const instance: PlaylistReadingExtensionInstance = {
        playingState,

        hasNext,
        hasPrevious,

        transformShortSubTitle: ({ data, label }) => {
          const current = data.value;
          const firstPlaylist = current?.playlists[0];
          return firstPlaylist?.title ?? label;
        },

        transformSubTitle: ({ data, label }) => {
          const current = data.value;
          const firstPlaylist = current?.playlists[0];
          return firstPlaylist?.title ?? label;
        },

        // A saved playlist plays at its own path,
        // `/{lang}/playlist/{locator}/{title}/{step}` (step 1-based), in place
        // of the chapter's. Ad-hoc queues have no record to point at, so they
        // keep the chapter's path and the query params below.
        transformUrlPath: ({ pathname }) => {
          const current = data.value;
          const firstPlaylist = current?.playlists[0];
          if (
            !current ||
            !firstPlaylist ||
            !isRecordedPlaylist(firstPlaylist) ||
            current.queue.length === 0
          ) {
            return pathname;
          }
          return buildSharedPagePath({
            kind: "playlist",
            language: i18n.language.peek(),
            locator: getPlaylistLocator(firstPlaylist),
            title: firstPlaylist.title,
            step: Math.max(0, current.step) + 1,
          });
        },

        // Always active (independent of `isShared`): contributes the
        // `playlist`/`playlistStep` query params for an ad-hoc queue's URL,
        // and clears them for a saved playlist, whose path carries both.
        // Falls back to the last-seen locator while nothing is playing so the
        // param doesn't flash-clear during an async deep-link load.
        transformQueryParams: ({ queryParams }) => {
          const current = data.value;
          const firstPlaylist = current?.playlists[0];
          if (firstPlaylist && isRecordedPlaylist(firstPlaylist)) {
            return { ...queryParams, playlist: null, playlistStep: null };
          }
          // Emit `playlistStep` only when a playlist is actually loaded; an
          // enablement with no playlist reads as "nothing playing" (step
          // absent), with `playlist` falling back to the last-seen locator.
          return {
            ...queryParams,
            playlist: firstPlaylist
              ? getPlaylistLocator(firstPlaylist)
              : initialPlaylistLocator.value,
            playlistStep: firstPlaylist ? current!.step.toString() : null,
          };
        },
        // Navigation hooks are unconditional now that playback state is synced:
        // any participant can drive next/previous and it propagates via `data`.
        // These return synchronously when they decline to intervene. Chapter
        // navigation writes its new position before the first `await`, so an
        // `async` hook here would put a microtask in front of every press —
        // enough for two quick presses to compute the same target.
        // At the ends of the queue these fall through to `default` rather than
        // `prevent`, so the reader keeps navigating chapter by chapter once the
        // queue is exhausted instead of going dead (see `hasNext` above). The
        // one exception is the first step past a saved playlist's last item,
        // which shows the finished modal instead.
        navigateNext: () => {
          if (playingState.queue.value.length === 0) {
            return { type: "default" };
          }
          if (!playingState.hasNext.value) {
            if (
              !playingState.canFinish.value ||
              playingState.finishPromptShown.value
            ) {
              return { type: "default" };
            }
            return playingState
              .next()
              .then(() => ({ type: "prevent" }) as const);
          }
          return playingState.next().then(() => ({ type: "prevent" }) as const);
        },
        navigatePrevious: () => {
          if (
            playingState.queue.value.length === 0 ||
            !playingState.hasPrevious.value
          ) {
            return { type: "default" };
          }
          return playingState
            .previous()
            .then(() => ({ type: "prevent" }) as const);
        },
        // Keeps the mobile swipe preview honest: while playing, the next chapter
        // is the queue's next scripture step, not the chapter that happens to
        // follow this one. A plan session whose readings jump between books
        // (Genesis 1, then Exodus 2) used to animate in Genesis 2 and then land
        // on Exodus 2 — the swipe looked broken.
        getAdjacentChapter: ({ direction }) => {
          const queue = playingState.queue.value;
          if (queue.length === 0) {
            return undefined; // nothing playing — the reader's own neighbour
          }
          const stepIndex =
            playingState.currentIndex.value + (direction === "next" ? 1 : -1);
          const step = queue[stepIndex];
          if (!step) {
            if (
              direction === "next" &&
              playingState.canFinish.value &&
              !playingState.finishPromptShown.value
            ) {
              // This step shows the finished modal and stays put.
              return null;
            }
            // Past the queue's edge, navigation falls through to the reader's
            // own chapter stepping, so preview that instead.
            return undefined;
          }
          if (step.type !== "bible-verse") {
            // A text/link step doesn't move the reader (it opens a modal), so
            // the chapter on screen is what stays. Nothing to preview.
            return null;
          }
          return {
            translationId: step.translationId,
            bookId: step.ref.bookId,
            chapter: step.ref.chapter,
          };
        },
        dispose: () => {
          disposeOut();
          disposeIn();
          disposeStepUrl();
          playingState.dispose();
          // Its Close stops this playback; with the playback gone some other
          // way (stopped, or by a session peer) it would stop the wrong one.
          modals.closeModal(PLAYLIST_FINISHED_MODAL_ID);
        },
      };
      // The registry erases `TData` to `unknown`; the extra `playingState`
      // property and the data-typed hooks are safe here (the hooks read
      // playback from the closure, not `ctx.data`).
      return instance as unknown as ReadingExtensionInstance;
    },
  });

  const playlistPageLoader = createSharedPageLoader({
    os,
    navigation,
    kind: "playlist",
    schema: PlaylistSchema,
    authorUserId: (playlist) => playlist.authorUserId,
    initialSeed: initialPlaylistPageSeed,
  });
  const playlistPage = playlistPageLoader.page;

  /**
   * Author names already looked up, by user id, so playback doesn't re-read
   * a profile every time it starts. A lookup that found no name isn't kept,
   * so a later playback can try again.
   */
  const authorNameCache = new Map<string, Promise<string | null>>();
  const getAuthorName = (userId: string): Promise<string | null> => {
    const cached = authorNameCache.get(userId);
    if (cached) {
      return cached;
    }
    const lookup = loadSharedPageAuthorName(os, userId).then((name) => {
      if (!name) {
        authorNameCache.delete(userId);
      }
      return name;
    });
    authorNameCache.set(userId, lookup);
    return lookup;
  };

  /**
   * The display name of whoever made the playlist (or reading plan) that's
   * playing, for the player. Null while it loads, and for queues no one
   * authored or whose author has no name.
   */
  const playingAuthorName = signal<string | null>(null);
  effect(() => {
    const userId = playing.value?.playlists.value[0]?.authorUserId;
    playingAuthorName.value = null;
    if (!userId) {
      return;
    }
    // Started from a playlist page, the name is already loaded.
    const page = playlistPage.peek();
    if (page?.item.authorUserId === userId && page.authorName) {
      playingAuthorName.value = page.authorName;
      return;
    }
    let current = true;
    void getAuthorName(userId).then((name) => {
      if (current) {
        playingAuthorName.value = name;
      }
    });
    return () => {
      current = false;
    };
  });

  /**
   * Starts the current playlist page's playlist from its first item and
   * leaves the playlist page for the reader.
   */
  const startPlaylistPage = (): void => {
    const page = playlistPage.peek();
    if (!page) {
      return;
    }
    // Push a new history entry before playback starts, so Back from playing
    // returns to the playlist's page, its home. Starting first would rewrite
    // the page's own entry into the first step's path (enabling playback
    // replaces the current URL), leaving nothing to go back to.
    tabs.leaveStaticPage({ push: true });
    startPlaying(page.item, 0, { sharedOpening: true });
  };

  /**
   * The step the URL is playing, 1-based, when it's a playing playlist's own
   * path; null on the playlist's page itself or anywhere else.
   */
  const playlistPageStep = computed<number | null>(
    () =>
      parsePlaylistPagePath(
        navigation.currentUrl.value.pathname,
        navigation.basePath
      )?.step ?? null
  );

  // Inbound (URL -> state) half of the `playlist`/`playlistStep` sync. Reads
  // both params together so pasting a link with a nonzero step while
  // something else is playing resolves to the right playlist *and* step in
  // one coordinated call, rather than racing an async playlist load against a
  // synchronous jump on the (still old) previous playing state.
  //
  // Every playback-state read below uses `.peek()` deliberately: this must be
  // an effect over `navigation.currentUrl` alone. If it also tracked `playing`
  // (etc.), it would re-run every time playback state changes for any reason
  // (e.g. `startPlaying` itself) and, seeing the URL hasn't caught up yet
  // (that happens separately, via `transformQueryParams`/`TabsManager`), would
  // wrongly treat the still-stale URL as an external "stop playback" request.
  //
  // The locator on the URL *before* this change. Closing Profile or playlist
  // history rewrites the URL, and that write can land before playback has
  // put its locator there. A URL that never asked for playback is not a
  // request to stop; leaving one that did (Back, a link without it) is.
  let previousPlaylistLocator: string | null =
    playbackRequestFromUrl(navigation.currentUrl.peek(), navigation.basePath)
      ?.locator ?? null;

  const syncPlayingFromUrl = () => {
    const request = playbackRequestFromUrl(
      navigation.currentUrl.value,
      navigation.basePath
    );
    const priorPlaylistLocator = previousPlaylistLocator;
    previousPlaylistLocator = request?.locator ?? null;

    const playingState = playing.peek();
    const firstPlaylist = playingState?.playlists.peek()[0];
    const locator = playingState
      ? firstPlaylist
        ? getPlaylistLocator(firstPlaylist)
        : null
      : initialPlaylistLocator.peek();

    if ((request?.locator ?? null) === locator) {
      if (
        playingState &&
        request?.stepIndex != null &&
        request.stepIndex !== playingState.currentIndex.peek()
      ) {
        void playingState.jumpTo(request.stepIndex);
      }
      return;
    }

    if (!request) {
      if (playing.peek() && priorPlaylistLocator == null) {
        return;
      }
      stopPlaying();
      return;
    }

    void startPlayingLocator(request.locator, request.stepIndex);
  };

  effect(() => {
    void navigation.currentUrl.value;
    syncPlayingFromUrl();
  });

  let resolveInitialPlayback: () => void = () => {};
  /**
   * Settles once the playback the initial URL asked for has started and the
   * reader has moved to its step (or failed to). The server waits on this so
   * a reload mid-playlist renders the step's chapter, and the client waits on
   * it before hydrating so its first render matches. Resolved immediately
   * when the URL asked for no playback.
   */
  const initialPlaybackPromise = new Promise<void>((resolve) => {
    resolveInitialPlayback = resolve;
  });

  if (initialPlaybackRequest) {
    // A playing path's playlist is what the page loader is already fetching
    // (or was seeded with); waiting for it saves loading it twice.
    const ready =
      parsePlaylistPagePath(navigation.initialUrl.pathname, navigation.basePath)
        ?.step != null
        ? playlistPageLoader.initialLoadPromise
        : Promise.resolve();
    void ready
      .then(() =>
        startPlayingLocator(
          initialPlaybackRequest.locator,
          initialPlaybackRequest.stepIndex
        )
      )
      .finally(() => {
        // The fallback locator only bridges this initial async load. Clear it
        // once settled so a later switch to a non-playing tab (whose URL has
        // no playback in it) isn't misread as a stale request for it.
        initialPlaylistLocator.value = null;
        resolveInitialPlayback();
      });
  } else {
    resolveInitialPlayback();
  }

  /**
   * Starts the playback the URL asks for on the active tab, if that tab
   * isn't already playing it. Restoring saved tabs after mount can replace
   * the tab the initial playback started on, and nothing about the URL
   * changes to trigger the sync above, so the app calls this after restoring.
   */
  const resumePlaybackFromUrl = (): void => {
    if (initialPlaylistLocator.peek()) {
      return; // The initial load is still starting it, on whatever tab is active then.
    }
    const request = playbackRequestFromUrl(
      navigation.currentUrl.peek(),
      navigation.basePath
    );
    if (!request) {
      return;
    }
    const firstPlaylist = playing.peek()?.playlists.peek()[0];
    if (
      firstPlaylist &&
      getPlaylistLocator(firstPlaylist) === request.locator
    ) {
      return;
    }
    void startPlayingLocator(request.locator, request.stepIndex);
  };

  /**
   * Builds the AI tools that let a provider edit whatever playlist is
   * currently open in the editor (`editingPlaylist`): add/update/delete items
   * and update the playlist metadata. Each tool reads `editingPlaylist` live
   * at call time, so it always targets the playlist being edited at that
   * moment rather than a fixed snapshot.
   */
  const getEditPlaylistTools = () => {
    const insertPlaylistItemTool = generateFunctionTool({
      name: "insertPlaylistItem",
      description: "Inserts an item into the playlist.",
      parameters: AIPlaylistItemSchema.extend({
        index: z.number(),
      }),
      function: async (args) => {
        try {
          return insertEditingPlaylistItem(
            args.index,
            convertToPlaylistItem(args)
          );
        } catch (err) {
          return `error: ${err instanceof Error ? err.message : String(err)}`;
        }
      },
    });

    const updatePlaylistItemTool = generateFunctionTool({
      name: "updatePlaylistItem",
      description: "Updates an item in the playlist.",
      parameters: AIPlaylistItemSchema.extend({
        index: z.number(),
      }),
      function: async (args) => {
        try {
          return updateEditingPlaylistItem(
            args.index,
            convertToPlaylistItem(args)
          );
        } catch (err) {
          return `error: ${err instanceof Error ? err.message : String(err)}`;
        }
      },
    });

    const movePlaylistItemTool = generateFunctionTool({
      name: "movePlaylistItem",
      description: "Moves an item in the playlist.",
      parameters: z.object({
        originalIndex: z.number(),
        newIndex: z.number(),
      }),
      function: async (args) =>
        reorderEditingPlaylistItem(args.originalIndex, args.newIndex),
    });

    const deletePlaylistItemTool = generateFunctionTool({
      name: "deletePlaylistItem",
      description: "Deletes an item from the playlist.",
      parameters: z.object({
        index: z.number(),
      }),
      function: async (args) => removeEditingPlaylistItem(args.index),
    });

    const updatePlaylistTool = generateFunctionTool({
      name: "updatePlaylistMetadata",
      description: "Updates the playlist metadata (title, description)",
      parameters: z.object({
        title: z.string(),
        description: z.string().nullable(),
      }),
      function: async (args) => {
        const current = editingPlaylist.value;
        if (!current) {
          return "error: no playlist is currently being edited";
        }

        return updateEditingPlaylistMetadata({
          title: args.title,
          description: args.description ?? current.description ?? null,
        });
      },
    });

    const getPlaylistStateTool = generateFunctionTool({
      name: "getPlaylistState",
      description:
        "Returns the current, live contents of the playlist being edited (title, description, and items). Always call this to check the playlist's actual state instead of relying on anything said earlier in the conversation — earlier tool results may no longer be accurate (e.g. if the user undid or discarded a change).",
      parameters: z.object({}),
      function: async () => {
        const current = editingPlaylist.value;
        if (!current) {
          return "error: no playlist is currently being edited";
        }
        return JSON.stringify(buildGeneratedPlaylist(current));
      },
    });

    return [
      updatePlaylistTool.tool,
      insertPlaylistItemTool.tool,
      updatePlaylistItemTool.tool,
      deletePlaylistItemTool.tool,
      movePlaylistItemTool.tool,
      getPlaylistStateTool.tool,
    ];
  };

  /** Converts a `Playlist` into the AI-facing `GeneratedPlaylist` shape. */
  const buildGeneratedPlaylist = (playlist: Playlist): GeneratedPlaylist => ({
    title: playlist.title ?? null,
    description: playlist.description ?? null,
    items: playlist.items.map((i) => convertToAiPlaylistItem(i)),
  });

  // While a playlist is open in the editor, expose the playlist-editing tools
  // to every AI chat via `ChatsManager`, so an AI participant can add/update/
  // remove items and edit the title/description as the user asks. Withdrawn
  // as soon as the user leaves the editor (save or cancel), so the tools
  // aren't offered outside of an actual editing session.
  effect(() => {
    if (!editingPlaylist.value || !isDiscoverOpen.value) {
      chats.removeContext(PLAYLIST_EDITOR_CHAT_CONTEXT_ID);
      return;
    }

    const generatedPlaylist = buildGeneratedPlaylist(editingPlaylist.value);
    const playlistLabel = editingPlaylist.value.title
      ? `"${editingPlaylist.value.title}"`
      : "this untitled playlist";
    chats.addContext({
      id: PLAYLIST_EDITOR_CHAT_CONTEXT_ID,
      label: {
        key: "playlist-editor",
        defaultValue: "Playlist Editor",
      },
      instructions: `The user is currently creating or editing a Bible reading playlist (${playlistLabel}). Use the provided tools to add, update, or remove playlist items, and to update the playlist's title and description, as the user asks. These tools ONLY ever apply to this one open playlist — never use them to satisfy a request for a different or additional playlist; if the user asks to create or edit some other playlist while this one is still open, tell them to close this editor first instead of reusing these tools on the wrong playlist. The snapshot below reflects the playlist's live, current state as of this message, and supersedes anything said earlier in the conversation (e.g. an item that was removed and then restored by cancelling); call getPlaylistState first if you need to confirm the current contents before answering a question about them. Playlist: ${JSON.stringify(generatedPlaylist)}`,
      tools: getEditPlaylistTools(),
    });
  });

  return {
    savePlaylist,
    deletePlaylist,
    clearHeroImage,
    createNewPlaylist,
    editPlaylist,
    saveEditingPlaylist,
    updateEditingPlaylistMetadata,
    uploadHeroImage,
    addEditingPlaylistItem,
    insertEditingPlaylistItem,
    updateEditingPlaylistItem,
    removeEditingPlaylistItem,
    reorderEditingPlaylistItem,
    cancelEditingPlaylist,
    isEditingPlaylistDirty,
    listPlaylists,
    getUserPlaylists,
    loadPlaylist,
    userPlaylists,
    userPlaylistHistory,
    availablePlaylists,
    view,
    actualView,
    editingPlaylist,
    playing,
    openingPlayback,
    showCurrentItem,
    startPlaying,
    stopPlaying,
    continueFromHistory,
    replayFromHistory,
    playFromHistory,
    removePlayHistory,
    getPlaylistUrl,
    playlistPage,
    playlistPageStep,
    initialPlaybackPromise,
    resumePlaybackFromUrl,
    playingAuthorName,
    playlistPageNotFound: playlistPageLoader.notFound,
    playlistPageLoadFailed: playlistPageLoader.loadFailed,
    playlistPageRetrying: playlistPageLoader.retrying,
    retryPlaylistPage: playlistPageLoader.retry,
    initialPlaylistPageLoadPromise: playlistPageLoader.initialLoadPromise,
    getPlaylistPageSeed: playlistPageLoader.getSeed,
    startPlaylistPage,
    isDiscoverOpen,
    goBackFromPlayingView,
    isMobile,
  };
}
